/**
 * Workflow Automation Node Configurations
 * Configuration panels for all workflow automation nodes
 */

import { useState, useEffect, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { FolderOpen, Plus, Trash2, RefreshCw, Search, Loader2, CheckCircle2, AlertCircle, Info, Sparkles, Maximize2, X, Copy, Check, Eye, Wand2, Braces, SplitSquareHorizontal, Code } from 'lucide-react';
// import { FolderOpen, Plus, Trash2, RefreshCw, Search, Loader2, CheckCircle2, AlertCircle, Info, Sparkles, X, Wand2 } from 'lucide-react';
import { AIProviderConfig } from './nodes/AIProviderConfig';
import { IntegrationConnectionUI } from './IntegrationConnectionUI';
import MonacoEditor from '@monaco-editor/react';
import { apiFetch } from '../../api';
import { fetchDatasources, fetchAllUsers } from './nodes/workflowNodeCache';
import { useAuth } from '@/context/useAuth';
import { getConnections, Connection } from '@/api/datamodels';
// import { apiFetch } from '@/api';
import { useServiceConnections } from '@/hooks/useServiceConnections';

export const ServiceConnectionSelect = ({
    service,
    value,
    onChange,
    label = "Connection"
}: {
    service: string;
    value: string;
    onChange: (val: string) => void;
    label?: string;
}) => {
    const { connections, loading } = useServiceConnections(service);

    return (
        <div className="mb-4">
            <Label className="text-sm font-semibold text-gray-700">{label}</Label>
            <Select value={value || 'new'} onValueChange={onChange}>
                <SelectTrigger className="w-full mt-1">
                    <SelectValue placeholder="Select Connection" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="new">+ Add New Credentials (Inline)</SelectItem>
                    {connections.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                            {c.is_default ? ` Default (${c.label})` : c.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            {loading && <p className="text-xs text-gray-400 mt-1">Loading connections...</p>}
        </div>
    );
};

const API_BASE_URL = (() => { const base = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, ''); return base.endsWith('/api/v1') ? base : base + '/api/v1'; })();

const getToken = () => {
    try {
        const sessionToken = sessionStorage.getItem('access_token');
        if (sessionToken) return sessionToken;

        const token = localStorage.getItem('token') || localStorage.getItem('authToken');
        if (token) return token;

        const storage = localStorage.getItem('auth-storage');
        if (storage) return JSON.parse(storage).state?.token || null;
    } catch (e) {
        console.error("Failed to parse auth token", e);
    }
    return null;
};

const parseTextFieldSelection = (value: any): string[] => {
    if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return [];
        if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
            try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed)) return parsed.map((v) => String(v).trim()).filter(Boolean);
            } catch {
                // Fall through to comma-separated parsing
            }
        }
        return trimmed.split(',').map((v) => v.trim()).filter(Boolean);
    }
    return [];
};

// ─── Reusable AI Generate Button ─────────────────────────────────────────────
interface AIGenerateButtonProps {
    /** What kind of content to generate, shown in the dialog title */
    contentType: string;
    /** Hint shown in the instruction textarea */
    placeholder?: string;
    /** Called with the generated text when user accepts */
    onGenerate: (text: string) => void;
    /** Optional context string (e.g. available fields) injected into the AI prompt */
    context?: string;
}

export const AIGenerateButton = ({ contentType, placeholder, onGenerate, context }: AIGenerateButtonProps) => {
    const [open, setOpen] = useState(false);
    const [instruction, setInstruction] = useState('');
    const [result, setResult] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleGenerate = async () => {
        if (!instruction.trim()) return;
        setLoading(true);
        setError('');
        setResult('');
        try {
            const token = getToken();
            const res = await fetch(`${API_BASE_URL}/ai/generate-text`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    contentType,
                    instruction,
                    context: context || ''
                })
            });
            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();
            setResult(data.text || data.result || '');
        } catch (e: any) {
            setError(e.message || 'Generation failed');
        } finally {
            setLoading(false);
        }
    };

    const handleAccept = () => {
        if (result) {
            onGenerate(result);
            setOpen(false);
            setInstruction('');
            setResult('');
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                title={`Generate ${contentType} with AI`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium
                           bg-gradient-to-r from-purple-500 to-violet-600 text-white
                           shadow-sm hover:shadow-purple-300 hover:shadow-md
                           hover:from-purple-600 hover:to-violet-700
                           transition-all duration-150 cursor-pointer border-0 outline-none"
            >
                <Sparkles className="h-3 w-3" />
                Generate
            </button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg bg-white">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-purple-600" />
                            Generate {contentType} with AI
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Describe what you want</Label>
                            <Textarea
                                value={instruction}
                                onChange={(e) => setInstruction(e.target.value)}
                                placeholder={placeholder || `Describe the ${contentType} you need...`}
                                rows={3}
                                className="mt-1"
                            />
                        </div>

                        {context && (
                            <div className="bg-gray-50 p-2 rounded text-[10px] text-gray-500 border">
                                <span className="font-medium">Available context:</span>{context}
                            </div>
                        )}

                        <Button
                            onClick={handleGenerate}
                            disabled={loading || !instruction.trim()}
                            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                            size="sm"
                        >
                            {loading ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Generating...</> : <><Sparkles className="h-3 w-3 mr-1" />Generate</>}
                        </Button>

                        {error && (
                            <div className="text-xs text-red-600 bg-red-50 p-2 rounded border border-red-200">
                                {error}
                            </div>
                        )}

                        {result && (
                            <div>
                                <Label className="text-xs">Generated Result</Label>
                                <Textarea
                                    value={result}
                                    onChange={(e) => setResult(e.target.value)}
                                    rows={6}
                                    className="mt-1 font-mono text-xs"
                                />
                                <p className="text-[10px] text-gray-500 mt-1">You can edit the result before accepting.</p>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button
                            size="sm"
                            onClick={handleAccept}
                            disabled={!result}
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                        >
                            Use This
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
// ─────────────────────────────────────────────────────────────────────────────

// API helpers - match PropertiesPanel: base + /api/v1 for flows

interface BaseConfigProps {
    data: any;
    onChange: (key: string, value: any) => void;
    /** Upstream column names from the connected node's output */
    inputColumns?: string[];
    /** Flow ID -- needed by Python Library upload panel */
    flowId?: string;
}

// File Test Result Type
interface FileTestResult {
    success: boolean;
    error?: string;
    resolvedPath?: string;
    files: any[];
    totalFiles: number;
    newFiles: number;
    alreadyProcessed: number;
    message?: string;
}

// 1. File Listener Configuration
export const FileListenerConfig = ({ data, onChange }: BaseConfigProps) => {
    const [showFileBrowser, setShowFileBrowser] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    const [testResult, setTestResult] = useState<FileTestResult | null>(null);
    const storageType = data.storageType || 'local';

    const handleFolderSelect = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.webkitdirectory = true;
        input.onchange = (e: any) => {
            const files = e.target.files;
            if (files && files.length > 0) {
                // Get the folder path from the first file
                const fullPath = files[0].path || files[0].webkitRelativePath;
                const folderPath = fullPath.substring(0, fullPath.lastIndexOf('/')) || fullPath.substring(0, fullPath.lastIndexOf('\\'));
                onChange('path', folderPath);
            }
        };
        input.click();
    };

    const handleTestFiles = async () => {
        const pathOrFolderId = data.path || data.gdriveFolderId;
        if (!pathOrFolderId && storageType !== 'gdrive') return;
        if (storageType === 'gdrive' && !pathOrFolderId) return;

        setIsTesting(true);
        setTestResult(null);

        try {
            const payload: Record<string, unknown> = {
                storageType: data.storageType || 'local',
                path: data.path || data.gdriveFolderId || '',
                recursive: data.recursive || false,
                filePatterns: data.filePatterns || ['*.*'],
                onlyNewFiles: data.onlyNewFiles !== false
            };
            if (storageType === 's3') {
                if (data.awsAccessKey) payload.awsAccessKey = data.awsAccessKey;
                if (data.awsSecretKey) payload.awsSecretKey = data.awsSecretKey;
                if (data.awsRegion) payload.awsRegion = data.awsRegion;
            } else if (storageType === 'azure' && data.azureConnectionString) payload.azureConnectionString = data.azureConnectionString;
            else if (storageType === 'sharepoint') {
                if (data.tenantId) payload.tenantId = data.tenantId;
                if (data.clientId) payload.clientId = data.clientId;
                if (data.clientSecret) payload.clientSecret = data.clientSecret;
                if (data.sharepointDomain) payload.sharepointDomain = data.sharepointDomain;
                if (data.siteName) payload.siteName = data.siteName;
                if (data.libraryName) payload.libraryName = data.libraryName;
            } else if (storageType === 'gdrive' && data.gdriveServiceAccountKey) payload.gdriveServiceAccountKey = data.gdriveServiceAccountKey;
            else if (storageType === 'onedrive' && data.onedriveAccessToken) payload.onedriveAccessToken = data.onedriveAccessToken;

            const response = await fetch(`${API_BASE_URL}/flows/ops/file-listener/test`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getToken()}`
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            setTestResult(result);
        } catch (error: any) {
            setTestResult({
                success: false,
                error: error.message || 'Test failed',
                files: [],
                totalFiles: 0,
                newFiles: 0,
                alreadyProcessed: 0
            });
        } finally {
            setIsTesting(false);
        }
    };

    const handleResetState = async () => {
        const pathOrFolderId = data.path || data.gdriveFolderId;
        if (!pathOrFolderId) return;

        setIsResetting(true);

        try {
            const response = await fetch(`${API_BASE_URL}/flows/ops/file-listener/reset-state`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getToken()}`
                },
                body: JSON.stringify({
                    storageType: data.storageType || 'local',
                    path: pathOrFolderId
                })
            });

            const result = await response.json();

            // Clear test result and show success
            setTestResult({
                success: true,
                message: result.message || 'State reset successfully',
                files: [],
                totalFiles: 0,
                newFiles: 0,
                alreadyProcessed: 0
            });

            // Auto-test after reset
            setTimeout(() => handleTestFiles(), 500);
        } catch (error: any) {
            setTestResult({
                success: false,
                error: error.message || 'Reset failed',
                files: [],
                totalFiles: 0,
                newFiles: 0,
                alreadyProcessed: 0
            });
        } finally {
            setIsResetting(false);
        }
    };

    return (
        <div className="space-y-4">
            <div>
                <Label>Storage Type</Label>
                <Select
                    value={storageType}
                    onValueChange={(value) => onChange('storageType', value)}
                >
                    <SelectTrigger className="w-full bg-white border-gray-300">
                        <SelectValue placeholder="Select storage type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-300 shadow-lg">
                        <SelectItem value="local">Local Filesystem</SelectItem>
                        <SelectItem value="s3">AWS S3</SelectItem>
                        <SelectItem value="azure">Azure Blob Storage</SelectItem>
                        <SelectItem value="sharepoint">Microsoft SharePoint</SelectItem>
                        <SelectItem value="gdrive">Google Drive</SelectItem>
                        <SelectItem value="onedrive">Microsoft OneDrive</SelectItem>

                    </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                    Where your files are stored
                </p>
            </div>

            {storageType === 'local' && (
                <div className="flex border-b border-gray-200 mb-4 mt-2">
                    {['browse', 'upload'].map(tab => (
                        <button
                            key={tab}
                            className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${(data.fileMode === tab || (!data.fileMode && tab === 'browse'))
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            onClick={() => onChange('fileMode', tab)}
                        >
                            {tab === 'browse' ? 'Browse Server' : 'Upload File'}
                        </button>
                    ))}
                </div>
            )}

            {storageType !== 'local' && (
                <IntegrationConnectionUI
                    protocol={storageType}
                    connectionId={data.connectionId}
                    onChangeConnectionId={(v) => onChange('connectionId', v)}
                    newConnectionLabel={data._newConnLabel || ''}
                    newConnectionFields={data._newConnFields || {}}
                    onChangeNewLabel={(v) => onChange('_newConnLabel', v)}
                    onChangeNewFields={(v) => onChange('_newConnFields', v)}
                />
            )}

            {storageType === 'sharepoint' && (
                <>
                    <div className="border-t pt-2 mt-4">
                        <Label className="text-sm font-semibold text-gray-700">SharePoint Location</Label>
                    </div>
                    <div className="mt-3">
                        <Label>SharePoint Domain<span className="text-red-500">*</span></Label>
                        <div className="flex items-center gap-1">
                            <Input
                                value={data.sharepointDomain || ''}
                                onChange={(e) => onChange('sharepointDomain', e.target.value)}
                                placeholder="contoso"
                                className="flex-1"
                            />
                            <span className="text-sm text-gray-500">.sharepoint.com</span>
                        </div>
                    </div>
                    <div className="mt-3">
                        <Label>Site Name<span className="text-red-500">*</span></Label>
                        <Input
                            value={data.siteName || ''}
                            onChange={(e) => onChange('siteName', e.target.value)}
                            placeholder="TeamSite"
                        />
                    </div>
                    <div className="mt-3">
                        <Label>Document Library</Label>
                        <Input
                            value={data.libraryName || 'Documents'}
                            onChange={(e) => onChange('libraryName', e.target.value)}
                            placeholder="Documents"
                        />
                    </div>
                </>
            )}

            {storageType === 'gdrive' && (
                <div className="mt-3">
                    <Label>Folder ID<span className="text-red-500">*</span></Label>
                    <Input
                        value={data.path || data.gdriveFolderId || ''}
                        onChange={(e) => { const v = e.target.value; onChange('path', v); onChange('gdriveFolderId', v); }}
                        placeholder="Folder ID from drive.google.com/drive/folders/XXXXX"
                    />
                </div>
            )}

            {(storageType !== 'local' || !data.fileMode || data.fileMode === 'browse') && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                    <Label>
                        {storageType === 'local' && 'Folder Path'}
                        {storageType === 's3' && 'S3 Bucket Path'}
                        {storageType === 'azure' && 'Azure Container Path'}
                        {storageType === 'sharepoint' && 'SharePoint Folder Path'}
                        {storageType === 'gdrive' && 'Google Drive Folder ID'}
                        {storageType === 'onedrive' && 'OneDrive Path'}

                    </Label>
                    <div className="flex gap-2">
                        <Input
                            value={data.path || ''}
                            onChange={(e) => onChange('path', e.target.value)}
                            placeholder={
                                storageType === 'local' ? 'C:\\path\\to\\folder or /path/to/folder' :
                                    storageType === 's3' ? 's3://bucket-name/prefix' :
                                        storageType === 'azure' ? 'azure://container-name/path' :
                                            storageType === 'sharepoint' ? '/Shared Documents/FolderName' :
                                                storageType === 'gdrive' ? 'Folder ID from Google Drive' :
                                                    storageType === 'onedrive' ? '/Documents/MyFolder' :
                                                        'Enter path'
                            }
                        />
                        {storageType === 'local' && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={handleFolderSelect}
                                title="Browse for folder"
                            >
                                <FolderOpen className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        {storageType === 'local' && 'Local filesystem path'}
                        {storageType === 's3' && 'S3 path format: s3://bucket/prefix'}
                        {storageType === 'azure' && 'Azure path format: azure://container/path'}
                        {storageType === 'sharepoint' && 'Folder path INSIDE the document library (e.g., /GenAI/Internal). Do NOT include the library name.'}
                        {storageType === 'gdrive' && 'Right-click folder in Google Drive -&gt; Get link -&gt; Copy folder ID'}
                        {storageType === 'onedrive' && 'Path relative to your OneDrive root'}

                    </p>
                </div>
            )}

            {storageType === 'local' && data.fileMode === 'upload' && (
                <div className="space-y-2">
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 flex flex-col items-center justify-center bg-gray-50 hover:bg-white transition-colors">
                        <input
                            type="file"
                            className="hidden"
                            id="file-listener-upload"
                            // @ts-ignore - webkitdirectory is non-standard but widely supported
                            webkitdirectory="true"
                            directory="true"
                            multiple
                            onChange={async (e) => {
                                const files = e.target.files;
                                if (!files || files.length === 0) return;
                                const toastId = toast.loading(`Uploading folder (${files.length} files)...`);
                                try {
                                    const formData = new FormData();
                                    for (let i = 0; i < files.length; i++) {
                                        formData.append('files', files[i]);
                                        // Send the relative path so the backend can reconstruct the folder structure
                                        formData.append('paths', files[i].webkitRelativePath || files[i].name);
                                    }
                                    const token = getToken();
                                    const res = await fetch(`${API_BASE_URL}/flows/ops/upload-folder`, {
                                        method: 'POST',
                                        headers: { 'Authorization': `Bearer ${token}` },
                                        body: formData
                                    });
                                    if (!res.ok) throw new Error("Folder upload failed");
                                    const json = await res.json();
                                    if (json && json.filePath) {
                                        onChange('path', json.filePath);
                                        toast.success("Folder uploaded successfully!", { id: toastId });
                                    } else {
                                        toast.error("Upload failed", { id: toastId });
                                    }
                                } catch (err) {
                                    toast.error("Folder upload failed", { id: toastId });
                                }
                            }}
                        />
                        <label htmlFor="file-listener-upload" className="cursor-pointer flex flex-col items-center gap-2">
                            <div className="p-3 bg-blue-100 text-blue-600 rounded-full">
                                <FolderOpen className="h-6 w-6" />
                            </div>
                            <span className="text-sm font-medium text-slate-700">Click to Select Folder</span>
                            <span className="text-xs text-slate-400">Upload a folder containing files to process</span>
                            <span className="text-[10px] text-slate-400 italic mt-1">(Your browser might say "Upload" in the dialog, but it will select the folder)</span>
                        </label>
                    </div>
                    {data.path && (
                        <div className="text-xs text-green-600 bg-green-50 p-2 rounded border border-green-100 mt-2 truncate">
                            Uploaded: {data.path}
                        </div>
                    )}
                </div>
            )}


            <div className="flex items-center justify-between">
                <Label>Recursive Scan</Label>
                <Switch
                    checked={data.recursive || false}
                    onCheckedChange={(checked) => onChange('recursive', checked)}
                />
            </div>

            <div className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                        <Label>Only New Files</Label>
                        <p className="text-xs text-gray-500">Track and skip already processed files</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <div className="flex-none">
                            <Switch
                                checked={data.onlyNewFiles !== false}
                                onCheckedChange={(checked) => onChange('onlyNewFiles', checked)}
                            />
                        </div>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleResetState}
                            disabled={!(data.path || data.gdriveFolderId) || isResetting}
                            className="text-orange-600 border-orange-300 hover:bg-orange-50 text-xs"
                            title="Reset tracking state"
                        >
                            {isResetting ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                            Reset
                        </Button>
                    </div>
                </div>
            </div>

            <div className="space-y-1">
                <div className="flex items-center justify-between">
                    <Label>Fail on Empty</Label>
                    <Switch
                        checked={data.failOnEmpty || false}
                        onCheckedChange={(checked) => onChange('failOnEmpty', checked)}
                    />
                </div>
                <p className="text-xs text-gray-500">
                    If enabled, workflow fails when no files are found. If disabled (default), workflow succeeds with empty output.
                </p>
            </div>

            <div>
                <Label>File Patterns</Label>
                <Input
                    value={data.filePatterns?.join(', ') || '*.*'}
                    onChange={(e) => {
                        const raw = e.target.value;
                        const patterns = raw ? raw.split(',').map(s => s.trim()).filter(Boolean) : ['*.*'];
                        onChange('filePatterns', patterns.length ? patterns : ['*.*']);
                    }}
                    placeholder="*.pdf, *.docx, *.txt"
                />
                <p className="text-xs text-gray-500 mt-1">
                    Comma-separated patterns (e.g., *.pdf, *.docx)
                </p>
            </div>

            {(storageType === 's3' || storageType === 'azure' || storageType === 'sharepoint' ||
                storageType === 'onedrive' || storageType === 'gdrive') && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-3">
                        <p className="text-xs text-green-900 font-medium mb-1">Multi-User Support</p>
                        <p className="text-xs text-green-800">
                            Each workflow can have its own credentials. Your credentials are stored securely with this workflow and won't affect other users.
                        </p>
                    </div>
                )}

        </div>
    );
};

// 2. Document Parser Configuration
export const DocumentParserConfig = ({ data, onChange, inputColumns = [] }: BaseConfigProps) => {
    const [testFile, setTestFile] = useState<File | null>(null);
    const [isTestParsing, setIsTestParsing] = useState(false);
    const [testResult, setTestResult] = useState<{ success: boolean; textExtraction?: string; ocrExtraction?: string; aiExtraction?: string; error?: string } | null>(null);

    const handleTestParse = async () => {
        if (!testFile) return;
        setIsTestParsing(true);
        setTestResult(null);
        try {
            const formData = new FormData();
            formData.append('file', testFile);
            const res = await fetch(`${API_BASE_URL}/flows/ops/document-parser/test`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${getToken()}` },
                body: formData
            });
            const result = await res.json().catch(() => ({}));
            if (!res.ok) {
                setTestResult({ success: false, error: result.detail || result.error || 'Test failed' });
                return;
            }
            setTestResult({
                success: true,
                textExtraction: result.textExtraction,
                ocrExtraction: result.ocrExtraction,
                aiExtraction: result.aiExtraction
            });
        } catch (e: any) {
            setTestResult({ success: false, error: e.message || 'Test failed' });
        } finally {
            setIsTestParsing(false);
        }
    };

    const allowedDocTypes = data.allowedDocTypes || ['pdf', 'docx', 'xlsx', 'pptx', 'txt', 'rtf', 'html'];
    const processingMethods = data.processingMethods || ['text_processing'];
    const phiSelectionMode = data.phiSelectionMode || 'auto';  // 'auto' or 'custom'
    const phiComponents = data.phiComponents || ['ssn', 'phone', 'email', 'mrn', 'date', 'zipcode', 'account', 'ip_address', 'url', 'license'];
    const customSensitivePatterns = data.customSensitivePatterns || [];

    const toggleDocType = (type: string) => {
        const current = data.allowedDocTypes || ['pdf', 'docx', 'xlsx', 'pptx', 'txt', 'rtf', 'html'];
        if (current.includes(type)) {
            onChange('allowedDocTypes', current.filter((t: string) => t !== type));
        } else {
            onChange('allowedDocTypes', [...current, type]);
        }
    };

    const toggleProcessingMethod = (method: string) => {
        const current = data.processingMethods || ['text_processing'];
        if (current.includes(method)) {
            onChange('processingMethods', current.filter((m: string) => m !== method));
        } else {
            onChange('processingMethods', [...current, method]);
        }
    };

    const togglePhiComponent = (component: string) => {
        const current = data.phiComponents || ['ssn', 'phone', 'email', 'mrn', 'date', 'zipcode', 'account', 'ip_address', 'url', 'license'];
        if (current.includes(component)) {
            onChange('phiComponents', current.filter((c: string) => c !== component));
        } else {
            onChange('phiComponents', [...current, component]);
        }
    };

    const addCustomPattern = () => {
        const patterns = [...customSensitivePatterns, { name: '', pattern: '', description: '' }];
        onChange('customSensitivePatterns', patterns);
    };

    const removeCustomPattern = (index: number) => {
        const patterns = customSensitivePatterns.filter((_: any, i: number) => i !== index);
        onChange('customSensitivePatterns', patterns);
    };

    const updateCustomPattern = (index: number, field: string, value: string) => {
        const patterns = [...customSensitivePatterns];
        patterns[index] = { ...patterns[index], [field]: value };
        onChange('customSensitivePatterns', patterns);
    };

    // Generate dynamic description based on selections
    const getSelectionSummary = () => {
        const docTypeLabels = allowedDocTypes.map((type: string) => {
            const typeMap: Record<string, string> = {
                pdf: 'PDF', docx: 'Word', xlsx: 'Excel', pptx: 'PowerPoint',
                txt: 'Text', rtf: 'RTF', html: 'HTML', jpg: 'Images'
            };
            return typeMap[type] || type;
        });

        const methodLabels = processingMethods.map((method: string) => {
            const methodMap: Record<string, string> = {
                text_processing: 'Text Extraction',
                image_processing: 'OCR',
                ai_processing: 'AI-Based'
            };
            return methodMap[method] || method;
        });

        return {
            docTypes: docTypeLabels.join(', ') || 'None',
            methods: methodLabels.join(' + ') || 'None'
        };
    };

    const summary = getSelectionSummary();

    return (
        <div className="space-y-4">
            {/* Test Parse Section */}
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <Label className="text-xs font-semibold text-slate-700 uppercase mb-2 block">Test Parse</Label>
                <p className="text-xs text-slate-600 mb-2">Upload a PDF or image to verify text extraction, OCR, and AI parsing.</p>
                <div className="flex gap-2 items-center">
                    <Input
                        type="file"
                        accept=".pdf,.docx,.jpg,.jpeg,.png,.tiff,.bmp"
                        onChange={(e) => { setTestFile(e.target.files?.[0] || null); setTestResult(null); }}
                        className="flex-1 text-xs"
                    />
                    <Button size="sm" onClick={handleTestParse} disabled={!testFile || isTestParsing} className="shrink-0">
                        {isTestParsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                        Test
                    </Button>
                </div>
                {testResult && (
                    <div className={`mt-3 p-3 rounded border text-xs ${testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                        {testResult.error ? (
                            <p className="text-red-700">{testResult.error}</p>
                        ) : (
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {testResult.textExtraction && (
                                    <div><span className="font-medium text-green-800">Text:</span><pre className="mt-1 whitespace-pre-wrap break-words text-slate-700">{testResult.textExtraction}</pre></div>
                                )}
                                {testResult.ocrExtraction && (
                                    <div><span className="font-medium text-blue-800">OCR:</span><pre className="mt-1 whitespace-pre-wrap break-words text-slate-700">{testResult.ocrExtraction}</pre></div>
                                )}
                                {testResult.aiExtraction && (
                                    <div><span className="font-medium text-purple-800">AI:</span><pre className="mt-1 whitespace-pre-wrap break-words text-slate-700">{testResult.aiExtraction}</pre></div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* File Path Column */}
            <div>
                <Label className="text-sm font-semibold mb-1 block">File Path Column</Label>
                <p className="text-xs text-slate-500 mb-2">
                    Column that contains the file URL or path.
                    Use this when running inside a Loop over pivot data (e.g. <code>nursing</code>or<code>physician</code>).
                    Leave blank to auto-detect any URL column.
                </p>
                {Array.isArray(inputColumns) && inputColumns.length > 0 ? (
                    <select
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                        value={data.filePathColumn || ''}
                        onChange={e => onChange('filePathColumn', e.target.value)}
                    >
                        <option value="">(auto-detect URL column)</option>
                        {(inputColumns as any[]).map((c: any) => {
                            const name = typeof c === 'string' ? c : c?.name || '';
                            return <option key={name} value={name}>{name}</option>;
                        })}
                    </select>
                ) : (
                    <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        placeholder="file_path  (leave blank to auto-detect)"
                        value={data.filePathColumn || ''}
                        onChange={e => onChange('filePathColumn', e.target.value)}
                    />
                )}
            </div>

            {/* Key Column -- carries an input column (e.g. account_no) into every parsed output row */}
            <div>
                <Label className="text-sm font-semibold mb-1 block">Key Column<span className="text-gray-400 font-normal">(optional)</span></Label>
                <p className="text-xs text-slate-500 mb-2">
                    Carry a column from the input row into each parsed output row so downstream nodes (e.g. Content Appender)
                    can link the extracted text back to the source record.
                    Example: set to <code>account_no</code> to preserve the account identifier across all parsed pages.
                </p>
                {Array.isArray(inputColumns) && inputColumns.length > 0 ? (
                    <select
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                        value={data.keyColumn || ''}
                        onChange={e => onChange('keyColumn', e.target.value)}
                    >
                        <option value="">(none -- don't carry a column)</option>
                        {(inputColumns as any[]).map((c: any) => {
                            const name = typeof c === 'string' ? c : c?.name || '';
                            return <option key={name} value={name}>{name}</option>;
                        })}
                    </select>
                ) : (
                    <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        placeholder="e.g. account_no  (leave blank to skip)"
                        value={data.keyColumn || ''}
                        onChange={e => onChange('keyColumn', e.target.value)}
                    />
                )}
            </div>

            {/* Document Types Section */}
            <div>
                <Label className="text-sm font-semibold mb-2 block">Document Types to Process</Label>
                <div className="grid grid-cols-2 gap-2 p-3 bg-gray-50 rounded-md border">
                    {[
                        { type: 'pdf', label: 'PDF', icon: '' },
                        { type: 'docx', label: 'Word (DOCX)', icon: '' },
                        { type: 'xlsx', label: 'Excel (XLSX)', icon: '' },
                        { type: 'pptx', label: 'PowerPoint (PPTX)', icon: '' },
                        { type: 'txt', label: 'Text Files', icon: '' },
                        { type: 'rtf', label: 'RTF', icon: '' },
                        { type: 'html', label: 'HTML', icon: '' },
                        { type: 'jpg', label: 'Images (JPG/PNG)', icon: '' }
                    ].map(({ type, label, icon }) => (
                        <div
                            key={type}
                            onClick={() => toggleDocType(type)}
                            className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-all ${allowedDocTypes.includes(type)
                                ? 'bg-blue-100 border-2 border-blue-500 text-blue-900'
                                : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-100'
                                }`}
                        >
                            <span className="text-lg">{icon}</span>
                            <span className="text-xs font-medium">{label}</span>
                            {allowedDocTypes.includes(type) && (
                                <span className="ml-auto text-blue-600">v</span>
                            )}
                        </div>
                    ))}
                </div>
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                    <strong>Selected:</strong> {summary.docTypes}
                </div>
            </div>

            {/* Processing Methods Section */}
            <div>
                <Label className="text-sm font-semibold mb-2 block">Processing Methods</Label>
                <div className="space-y-2 p-3 bg-gray-50 rounded-md border">
                    <div
                        onClick={() => toggleProcessingMethod('text_processing')}
                        className={`flex items-start gap-3 p-3 rounded cursor-pointer transition-all ${processingMethods.includes('text_processing')
                            ? 'bg-green-100 border-2 border-green-500'
                            : 'bg-white border border-gray-300 hover:bg-gray-100'
                            }`}
                    >
                        <div className="flex-shrink-0 mt-0.5">
                            {processingMethods.includes('text_processing') ? (
                                <span className="text-green-600 text-lg">[x]</span>
                            ) : (
                                <span className="text-gray-400 text-lg">[ ]</span>
                            )}
                        </div>
                        <div className="flex-grow">
                            <div className="font-medium text-sm">Text Processing</div>
                            <div className="text-xs text-gray-600 mt-0.5">
                                Fast extraction for clean digital documents
                                {allowedDocTypes.some((t: string) => ['pdf', 'docx', 'xlsx', 'pptx', 'txt', 'rtf', 'html'].includes(t)) &&
                                    ` (Supports: ${allowedDocTypes.filter((t: string) => ['pdf', 'docx', 'xlsx', 'pptx', 'txt', 'rtf', 'html'].includes(t)).map((t: string) => t.toUpperCase()).join(', ')})`
                                }
                            </div>
                        </div>
                    </div>

                    <div
                        onClick={() => toggleProcessingMethod('image_processing')}
                        className={`flex items-start gap-3 p-3 rounded cursor-pointer transition-all ${processingMethods.includes('image_processing')
                            ? 'bg-green-100 border-2 border-green-500'
                            : 'bg-white border border-gray-300 hover:bg-gray-100'
                            }`}
                    >
                        <div className="flex-shrink-0 mt-0.5">
                            {processingMethods.includes('image_processing') ? (
                                <span className="text-green-600 text-lg">[x]</span>
                            ) : (
                                <span className="text-gray-400 text-lg">[ ]</span>
                            )}
                        </div>
                        <div className="flex-grow">
                            <div className="font-medium text-sm">Image/OCR Processing</div>
                            <div className="text-xs text-gray-600 mt-0.5">
                                OCR for scanned PDFs/images (pytesseract or easyocr)
                                {allowedDocTypes.some((t: string) => ['pdf', 'jpg'].includes(t)) &&
                                    ` (Supports: ${allowedDocTypes.filter((t: string) => ['pdf', 'jpg'].includes(t)).map((t: string) => t.toUpperCase()).join(', ')})`
                                }
                            </div>
                        </div>
                    </div>

                    <div
                        onClick={() => toggleProcessingMethod('ai_processing')}
                        className={`flex items-start gap-3 p-3 rounded cursor-pointer transition-all ${processingMethods.includes('ai_processing')
                            ? 'bg-green-100 border-2 border-green-500'
                            : 'bg-white border border-gray-300 hover:bg-gray-100'
                            }`}
                    >
                        <div className="flex-shrink-0 mt-0.5">
                            {processingMethods.includes('ai_processing') ? (
                                <span className="text-green-600 text-lg">[x]</span>
                            ) : (
                                <span className="text-gray-400 text-lg">[ ]</span>
                            )}
                        </div>
                        <div className="flex-grow">
                            <div className="font-medium text-sm">AI-Based Processing</div>
                            <div className="text-xs text-gray-600 mt-0.5">
                                AI extraction via marker-pdf for complex layouts
                                {allowedDocTypes.includes('pdf') && ' (PDF only, pip install marker-pdf)'}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800">
                    <strong>Selected Methods:</strong> {summary.methods}
                </div>
                {processingMethods.includes('image_processing') && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                        <strong>OCR:</strong>Uses pytesseract (needs tesseract-ocr system package) or easyocr. Install:<code>pip install pytesseract easyocr</code>
                        <br />System:<code>sudo apt install tesseract-ocr</code>(or<code>choco install tesseract</code> on Windows)
                    </div>
                )}
            </div>

            {/* Protected Data Handling Section */}
            <div className="border-t pt-4">
                <Label className="text-sm font-semibold mb-2 block">
                    Protected Data Handling
                </Label>
                <div>
                    <Label>Protection Mode</Label>
                    <Select
                        value={data.phiMode || 'none'}
                        onValueChange={(value) => onChange('phiMode', value)}
                    >
                        <SelectTrigger className="w-full bg-white border-gray-300">
                            <SelectValue placeholder="Select protection mode" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-300 shadow-lg">
                            <SelectItem value="none">
                                <div className="flex flex-col">
                                    <span className="font-medium">None</span>
                                    <span className="text-xs text-gray-500">No protection (default)</span>
                                </div>
                            </SelectItem>
                            <SelectItem value="detect_only">
                                <div className="flex flex-col">
                                    <span className="font-medium">[!] Detect Only</span>
                                    <span className="text-xs text-gray-500">Flag protected data but keep original text</span>
                                </div>
                            </SelectItem>
                            <SelectItem value="redact">
                                <div className="flex flex-col">
                                    <span className="font-medium">Redact</span>
                                    <span className="text-xs text-gray-500">Replace protected data with [REDACTED] tags</span>
                                </div>
                            </SelectItem>
                            <SelectItem value="deidentify">
                                <div className="flex flex-col">
                                    <span className="font-medium">De-identify</span>
                                    <span className="text-xs text-gray-500">Replace protected data with safe synthetic data</span>
                                </div>
                            </SelectItem>
                            <SelectItem value="encrypt_phi">
                                <div className="flex flex-col">
                                    <span className="font-medium">Encrypt</span>
                                    <span className="text-xs text-gray-500">Encrypt protected data elements</span>
                                </div>
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {data.phiMode && data.phiMode !== 'none' && (
                    <>
                        {/* Protected Data Selection Mode */}
                        <div className="mt-4">
                            <Label className="text-sm font-medium mb-2 block">Identifier Selection</Label>
                            <div className="flex gap-4 p-3 bg-gray-50 rounded-md border">
                                <div
                                    onClick={() => {
                                        onChange('phiSelectionMode', 'auto');
                                        // Auto mode: select all identifiers
                                        onChange('phiComponents', ['ssn', 'phone', 'email', 'mrn', 'date', 'zipcode', 'account', 'ip_address', 'url', 'license']);
                                    }}
                                    className={`flex-1 flex items-center gap-3 p-3 rounded cursor-pointer transition-all ${phiSelectionMode === 'auto'
                                        ? 'bg-blue-100 border-2 border-blue-500'
                                        : 'bg-white border border-gray-300 hover:bg-gray-100'
                                        }`}
                                >
                                    <div className="flex-shrink-0">
                                        {phiSelectionMode === 'auto' ? (
                                            <span className="text-blue-600 text-xl">(*)</span>
                                        ) : (
                                            <span className="text-gray-400 text-xl">( )</span>
                                        )}
                                    </div>
                                    <div className="flex-grow">
                                        <div className="font-medium text-sm">Auto (All Identifiers)</div>
                                        <div className="text-xs text-gray-600 mt-0.5">
                                            Automatically detect all 10 protected data types
                                        </div>
                                    </div>
                                </div>

                                <div
                                    onClick={() => onChange('phiSelectionMode', 'custom')}
                                    className={`flex-1 flex items-center gap-3 p-3 rounded cursor-pointer transition-all ${phiSelectionMode === 'custom'
                                        ? 'bg-blue-100 border-2 border-blue-500'
                                        : 'bg-white border border-gray-300 hover:bg-gray-100'
                                        }`}
                                >
                                    <div className="flex-shrink-0">
                                        {phiSelectionMode === 'custom' ? (
                                            <span className="text-blue-600 text-xl">(*)</span>
                                        ) : (
                                            <span className="text-gray-400 text-xl">( )</span>
                                        )}
                                    </div>
                                    <div className="flex-grow">
                                        <div className="font-medium text-sm">Custom (Manual Selection)</div>
                                        <div className="text-xs text-gray-600 mt-0.5">
                                            Choose specific identifiers relevant to your use case
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Protected Data Components Selection - Only shown in Custom mode */}
                        {phiSelectionMode === 'custom' && (
                            <div className="mt-4">
                                <Label className="text-sm font-medium mb-2 block">Select Identifiers to Protect</Label>
                                <div className="grid grid-cols-2 gap-2 p-3 bg-gray-50 rounded-md border max-h-64 overflow-y-auto">
                                    {[
                                        { id: 'ssn', label: 'SSN', icon: '🆔', desc: 'Social Security Numbers' },
                                        { id: 'phone', label: 'Phone', icon: '', desc: 'Phone numbers' },
                                        { id: 'email', label: 'Email', icon: '', desc: 'Email addresses' },
                                        { id: 'mrn', label: 'MRN', icon: '', desc: 'Medical Record Numbers' },
                                        { id: 'date', label: 'Dates', icon: '', desc: 'Birth dates, visit dates' },
                                        { id: 'zipcode', label: 'ZIP Code', icon: '', desc: 'Postal codes' },
                                        { id: 'account', label: 'Account #', icon: '', desc: 'Account numbers' },
                                        { id: 'ip_address', label: 'IP Address', icon: '', desc: 'IP addresses' },
                                        { id: 'url', label: 'URLs', icon: '', desc: 'Web addresses' },
                                        { id: 'license', label: 'License', icon: '', desc: 'Driver license, etc.' }
                                    ].map(({ id, label, icon, desc }) => (
                                        <div
                                            key={id}
                                            onClick={() => togglePhiComponent(id)}
                                            className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-all ${phiComponents.includes(id)
                                                ? 'bg-purple-100 border-2 border-purple-500'
                                                : 'bg-white border border-gray-300 hover:bg-gray-100'
                                                }`}
                                            title={desc}
                                        >
                                            <span className="text-base">{icon}</span>
                                            <div className="flex-grow">
                                                <div className="text-xs font-medium">{label}</div>
                                                <div className="text-[10px] text-gray-500">{desc}</div>
                                            </div>
                                            {phiComponents.includes(id) && (
                                                <span className="text-purple-600 text-sm">v</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    {phiComponents.length} identifier(s) selected. Unselected types will not be detected.
                                </p>
                            </div>
                        )}

                        {/* Custom Protected Data Patterns - Dynamic Example-Based */}
                        <div className="mt-4">
                            <div className="flex items-center justify-between mb-2">
                                <Label className="text-sm font-medium">Additional Protected Data to Detect</Label>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={addCustomPattern}
                                    className="h-7 text-xs"
                                >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Add Protected Data
                                </Button>
                            </div>

                            {customSensitivePatterns.length === 0 ? (
                                <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-dashed border-blue-300 rounded-lg text-xs">
                                    <p className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                                        <span className="text-lg"></span>
                                        Protect ANY Data Unique to Your Organization
                                    </p>
                                    <p className="text-blue-800 mb-3">
                                        Simply describe what you want to protect and show us examples. We'll figure out the pattern automatically!
                                    </p>
                                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                                        <div className="p-2 bg-white/60 rounded">
                                            <span className="font-medium">Example 1:</span><br />
                                            <span className="text-gray-700">"Employee Badge Numbers"</span><br />
                                            <span className="text-blue-600 font-mono">EMP-1234, EMP-5678</span>
                                        </div>
                                        <div className="p-2 bg-white/60 rounded">
                                            <span className="font-medium">Example 2:</span><br />
                                            <span className="text-gray-700">"Internal Project Codes"</span><br />
                                            <span className="text-blue-600 font-mono">PROJ-2024-ABC, PROJ-2025-XYZ</span>
                                        </div>
                                        <div className="p-2 bg-white/60 rounded">
                                            <span className="font-medium">Example 3:</span><br />
                                            <span className="text-gray-700">"Salary Information"</span><br />
                                            <span className="text-blue-600 font-mono">$75,000, $125,500.00</span>
                                        </div>
                                        <div className="p-2 bg-white/60 rounded">
                                            <span className="font-medium">Example 4:</span><br />
                                            <span className="text-gray-700">"Facility Access Cards"</span><br />
                                            <span className="text-blue-600 font-mono">CARD-AB-1234, CARD-XY-9876</span>
                                        </div>
                                    </div>
                                    <p className="text-blue-800 mt-3 text-center font-medium">
                                        Click "Add Protected Data" to protect any format you need (see button below)
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {customSensitivePatterns.map((pattern: any, index: number) => (
                                        <div key={index} className="p-4 bg-white border-2 border-gray-200 rounded-lg shadow-sm hover:border-blue-300 transition-all space-y-3">
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-lg"></span>
                                                    <Label className="text-sm font-semibold text-gray-700">Protected Data #{index + 1}</Label>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => removeCustomPattern(index)}
                                                    className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>

                                            {/* Step 1: Describe what this is */}
                                            <div>
                                                <Label className="text-xs text-gray-600 mb-1.5 block font-medium">
                                                    What data do you want to protect?
                                                </Label>
                                                <Input
                                                    placeholder="e.g., Employee Badge Numbers, Customer IDs, Internal Order Codes..."
                                                    value={pattern.name || ''}
                                                    onChange={(e) => updateCustomPattern(index, 'name', e.target.value)}
                                                    className="text-sm border-gray-300 focus:border-blue-400"
                                                />
                                                <p className="text-[10px] text-gray-500 mt-1 ml-1">
                                                    A simple description so you remember what this protects
                                                </p>
                                            </div>

                                            {/* Step 2: Show examples */}
                                            <div>
                                                <Label className="text-xs text-gray-600 mb-1.5 block font-medium">
                                                    Give 2-3 examples from your actual data
                                                </Label>
                                                <Textarea
                                                    placeholder="Paste real examples (one per line):&#10;EMP-1234&#10;EMP-5678&#10;EMP-9999"
                                                    value={pattern.examples || ''}
                                                    onChange={(e) => updateCustomPattern(index, 'examples', e.target.value)}
                                                    className="text-xs font-mono border-gray-300 focus:border-blue-400 min-h-[60px]"
                                                />
                                                <p className="text-[10px] text-gray-500 mt-1 ml-1">
                                                    The more examples, the better! Separate each with a new line.
                                                </p>
                                            </div>

                                            {/* Auto-generated pattern preview */}
                                            {pattern.examples && pattern.examples.trim() && (
                                                <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-300 rounded-lg">
                                                    <div className="flex items-start gap-2">
                                                        <span className="text-green-600 text-lg mt-0.5">v</span>
                                                        <div className="flex-grow">
                                                            <p className="text-xs text-green-900 font-semibold mb-1">
                                                                Pattern Auto-Generated!
                                                            </p>
                                                            <p className="text-[11px] text-green-800">
                                                                We'll detect any data matching these {pattern.examples.split('\\n').filter((e: string) => e.trim()).length} example(s)
                                                            </p>
                                                            {pattern.pattern && (
                                                                <p className="text-[10px] text-green-700 mt-1 font-mono bg-white/50 px-2 py-1 rounded">
                                                                    Pattern: {pattern.pattern}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Advanced: Manual pattern override */}
                                            <details className="text-xs">
                                                <summary className="cursor-pointer text-gray-600 hover:text-gray-800 font-medium">
                                                    Advanced: Override pattern manually
                                                </summary>
                                                <div className="mt-2 p-3 bg-gray-50 rounded border border-gray-200 space-y-2">
                                                    <Label className="text-[11px] text-gray-600">Custom Pattern (Regex or Simple Format)</Label>
                                                    <Input
                                                        placeholder="e.g., EMP-#### or EMP-\\d{4} for regex"
                                                        value={pattern.pattern || ''}
                                                        onChange={(e) => updateCustomPattern(index, 'pattern', e.target.value)}
                                                        className="text-xs font-mono"
                                                    />
                                                    <p className="text-[10px] text-gray-500 italic">
                                                        Use # for digits, @ for letters (e.g., EMP-####-@@@@)
                                                    </p>
                                                </div>
                                            </details>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <p className="text-xs text-gray-500 mt-2 italic">
                                Tip: The system learns from your examples. No technical knowledge needed!
                            </p>
                        </div>

                        <div className="flex items-center justify-between mt-3">
                            <div>
                                <Label>Audit Logging</Label>
                                <p className="text-xs text-gray-500">Log all protected data detections for compliance</p>
                            </div>
                            <Switch
                                checked={data.phiAuditLog !== false}
                                onCheckedChange={(checked) => onChange('phiAuditLog', checked)}
                            />
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
                            <p className="text-xs text-yellow-900 font-medium mb-1">Compliance Notice</p>
                            <p className="text-xs text-yellow-800">
                                <strong>Mode:</strong> {phiSelectionMode === 'auto' ? 'Auto (All 10 identifiers)' : `Custom (${phiComponents.length} selected)`}
                                {phiSelectionMode === 'custom' && phiComponents.length > 0 && (
                                    <><br /><strong>Identifiers:</strong>{phiComponents.map((c: string) => c.toUpperCase()).join(', ')}</>
                                )}
                                {customSensitivePatterns.length > 0 && (
                                    <><br /><strong>Custom Patterns:</strong>{customSensitivePatterns.length} pattern(s)</>
                                )}
                            </p>
                        </div>
                    </>
                )}
            </div>

            {/* PDF Crop / Trim Section */}
            <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <Label className="text-sm font-semibold">PDF Header / Footer Trim</Label>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Crop top/bottom margins before extracting text -- removes repeated headers, footers, and page numbers.
                        </p>
                    </div>
                    <Switch
                        checked={!!(data.pdfCrop?.enabled)}
                        onCheckedChange={(checked) =>
                            onChange('pdfCrop', { ...(data.pdfCrop || {}), enabled: checked })
                        }
                    />
                </div>

                {data.pdfCrop?.enabled && (
                    <div className="space-y-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs text-blue-800">
                            Dimensions are in PDF points (1 pt ~= 0.35 mm). Default values match standard nursing-chart headers.
                        </p>

                        {/* Header -- first page */}
                        <div className="flex items-center gap-3">
                            <div className="flex-1">
                                <Label className="text-xs font-medium text-gray-700">First-Page Header (pts)</Label>
                                <p className="text-[10px] text-gray-500">Trim from top of page 1 (or pages with PATIENT/DOB)</p>
                            </div>
                            <input
                                type="number"
                                min={0}
                                max={400}
                                className="w-20 text-sm px-2 py-1.5 border border-gray-300 rounded-md focus:border-blue-500 bg-white text-center"
                                value={data.pdfCrop?.headerFirst ?? 130}
                                onChange={(e) =>
                                    onChange('pdfCrop', { ...(data.pdfCrop || {}), headerFirst: Number(e.target.value) })
                                }
                            />
                        </div>

                        {/* Header -- other pages */}
                        <div className="flex items-center gap-3">
                            <div className="flex-1">
                                <Label className="text-xs font-medium text-gray-700">Other Pages Header (pts)</Label>
                                <p className="text-[10px] text-gray-500">Trim from top of all subsequent pages</p>
                            </div>
                            <input
                                type="number"
                                min={0}
                                max={400}
                                className="w-20 text-sm px-2 py-1.5 border border-gray-300 rounded-md focus:border-blue-500 bg-white text-center"
                                value={data.pdfCrop?.headerOthers ?? 45}
                                onChange={(e) =>
                                    onChange('pdfCrop', { ...(data.pdfCrop || {}), headerOthers: Number(e.target.value) })
                                }
                            />
                        </div>

                        {/* Footer */}
                        <div className="flex items-center gap-3">
                            <div className="flex-1">
                                <Label className="text-xs font-medium text-gray-700">Footer (pts)</Label>
                                <p className="text-[10px] text-gray-500">Trim from bottom of every page</p>
                            </div>
                            <input
                                type="number"
                                min={0}
                                max={200}
                                className="w-20 text-sm px-2 py-1.5 border border-gray-300 rounded-md focus:border-blue-500 bg-white text-center"
                                value={data.pdfCrop?.footerMargin ?? 35}
                                onChange={(e) =>
                                    onChange('pdfCrop', { ...(data.pdfCrop || {}), footerMargin: Number(e.target.value) })
                                }
                            />
                        </div>

                        <div className="bg-white border border-blue-200 rounded p-2 text-[10px] text-blue-700">
                            <strong>Current config:</strong> first-page header={data.pdfCrop?.headerFirst ?? 130} pt,
                            other headers={data.pdfCrop?.headerOthers ?? 45} pt,
                            footer={data.pdfCrop?.footerMargin ?? 35} pt
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// 3. Conditional Router Configuration
export const ConditionalRouterConfig = ({ data, onChange, inputColumns = [] }: BaseConfigProps) => {
    const conditions = data.conditions || [];
    const colNames = (Array.isArray(inputColumns) ? inputColumns : [])
        .map((c: any) => (typeof c === 'string' ? c : c?.name || c)).filter(Boolean);

    const addCondition = () => {
        onChange('conditions', [
            ...conditions,
            { id: crypto.randomUUID(), routeName: `route_${conditions.length + 1}`, condition: '' }
        ]);
    };

    const removeCondition = (index: number) => {
        onChange('conditions', conditions.filter((_: any, i: number) => i !== index));
    };

    const updateCondition = (index: number, field: string, value: string) => {
        const updated = [...conditions];
        updated[index] = { ...updated[index], [field]: value };
        onChange('conditions', updated);
    };

    return (
        <div className="space-y-4">
            {/* Standard Header */}
            <div className="bg-orange-50 p-3 rounded-lg border border-orange-100 flex items-start gap-3">
                <div className="mt-0.5 bg-orange-100 p-1.5 rounded text-orange-600 shrink-0">
                    <SplitSquareHorizontal className="h-4 w-4" />
                </div>
                <div>
                    <h3 className="text-xs font-semibold text-orange-900 mb-0.5">Route by Condition</h3>
                    <p className="text-[10px] text-orange-700 leading-relaxed">
                        Evaluates rows against SQL conditions and sends matching rows to the corresponding output handle.
                        If a row matches multiple conditions, it goes to the <strong>first</strong> match.
                    </p>
                </div>
            </div>

            <div className="flex justify-between items-center">
                <Label>Conditions</Label>
                <Button size="sm" onClick={addCondition}>
                    <Plus className="h-4 w-4 mr-1" /> Add Condition
                </Button>
            </div>

            {conditions.map((cond: any, idx: number) => (
                <div key={idx} className="border p-3 rounded-md space-y-2">
                    <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Condition {idx + 1}</span>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeCondition(idx)}
                        >
                            <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                    </div>

                    <div>
                        <Label>Route Name</Label>
                        <Input
                            value={cond.routeName || ''}
                            onChange={(e) => updateCondition(idx, 'routeName', e.target.value)}
                            placeholder="route_name"
                        />
                    </div>

                    <div>
                        <div className="flex justify-between items-center mb-1">
                            <Label>Condition (SQL Expression)</Label>
                            <AIGenerateButton
                                contentType="SQL Condition"
                                placeholder="e.g. amount is greater than 1000 and status is active"
                                onGenerate={(text) => updateCondition(idx, 'condition', text)}
                                context={
                                    (colNames.length > 0 ? `Available input columns: ${colNames.join(', ')}\n` : 'No input columns available\n') +
                                    'CRITICAL RULE: Return ONLY the SQL condition expression. Do NOT include the word "WHERE". Do not wrap in markdown quotes.'
                                }
                            />
                        </div>
                        <Textarea
                            value={cond.condition || ''}
                            onChange={(e) => updateCondition(idx, 'condition', e.target.value)}
                            placeholder="column_name > 100"
                            rows={2}
                        />
                    </div>
                </div>
            ))}

            <div>
                <Label>Default Route Name</Label>
                <Input
                    value={data.defaultRoute || 'default'}
                    onChange={(e) => onChange('defaultRoute', e.target.value)}
                    placeholder="default"
                />
            </div>
        </div>
    );
};

// 4. Conditional Split Configuration
export const ConditionalSplitConfig = ({ data, onChange, inputColumns = [], nodeId, onRemoveConditionEdges }: BaseConfigProps & { nodeId?: string; onRemoveConditionEdges?: (conditionId: string) => void }) => {
    const conditions = data.conditions || [];
    const colNames = (Array.isArray(inputColumns) ? inputColumns : [])
        .map((c: any) => (typeof c === 'string' ? c : c?.name || c)).filter(Boolean);

    const OPERATORS = [
        { value: '==', label: 'Equals (==)' },
        { value: '!=', label: 'Not Equals (!=)' },
        { value: '>', label: 'Greater Than (>)' },
        { value: '<', label: 'Less Than (<)' },
        { value: '>=', label: 'Greater or Equal (>=)' },
        { value: '<=', label: 'Less or Equal (<=)' },
        { value: 'contains', label: 'Contains' },
        { value: 'startsWith', label: 'Starts With' },
    ];

    const addCondition = () => {
        onChange('conditions', [
            ...conditions,
            { id: crypto.randomUUID(), column: '', operator: '==', value: '', outputLabel: `branch_${conditions.length + 1}` }
        ]);
    };

    const removeCondition = (index: number) => {
        const cond = conditions[index];
        // Clean up any edges connected to this condition's handle to prevent ghost/stale edges
        if (cond?.id && onRemoveConditionEdges) {
            onRemoveConditionEdges(cond.id);
        }
        onChange('conditions', conditions.filter((_: any, i: number) => i !== index));
    };

    const updateCondition = (index: number, field: string, value: string) => {
        const updated = [...conditions];
        updated[index] = { ...updated[index], [field]: value };
        onChange('conditions', updated);
    };

    return (
        <div className="space-y-4">
            {/* Info Banner */}
            <div className="p-3 bg-orange-50 text-orange-800 text-xs rounded-lg border border-orange-200 flex items-start gap-2">
                <Info className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
                <div>
                    <span className="font-semibold block mb-0.5">If / Else Split</span>
                    Each condition creates a named output branch. Rows that match no condition go to the <strong>Default</strong> output.
                </div>
            </div>

            <div className="flex justify-between items-center">
                <Label>Conditions</Label>
                <Button size="sm" onClick={addCondition}>
                    <Plus className="h-4 w-4 mr-1" /> Add Branch
                </Button>
            </div>

            {conditions.map((cond: any, idx: number) => (
                <div key={idx} className="border border-orange-200 bg-orange-50/40 p-3 rounded-lg space-y-2">
                    <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-orange-700">Branch {idx + 1}</span>
                        <Button size="sm" variant="ghost" onClick={() => removeCondition(idx)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                    </div>

                    {/* Output Label */}
                    <div>
                        <Label className="text-xs text-gray-600">Output Label<span className="text-gray-400 font-normal">(handle name)</span></Label>
                        <Input
                            value={cond.outputLabel || ''}
                            onChange={(e) => updateCondition(idx, 'outputLabel', e.target.value)}
                            placeholder={`branch_${idx + 1}`}
                            className="h-8 text-xs"
                        />
                    </div>

                    {/* Condition Row */}
                    <div className="grid grid-cols-2 gap-2">
                        {/* Column */}
                        <div>
                            <Label className="text-xs text-gray-600">Column</Label>
                            {colNames.length > 0 ? (
                                <Select value={cond.column || ''} onValueChange={(v) => updateCondition(idx, 'column', v)}>
                                    <SelectTrigger className="h-8 text-xs">
                                        <SelectValue placeholder="Select..." />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[200px] bg-white z-50 shadow-lg">
                                        {colNames.map((col: string) => (
                                            <SelectItem key={col} value={col}>{col}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <Input
                                    value={cond.column || ''}
                                    onChange={(e) => updateCondition(idx, 'column', e.target.value)}
                                    placeholder="column"
                                    className="h-8 text-xs font-mono"
                                />
                            )}
                        </div>

                        {/* Operator */}
                        <div>
                            <Label className="text-xs text-gray-600">Operator</Label>
                            <Select value={cond.operator || '=='} onValueChange={(v) => updateCondition(idx, 'operator', v)}>
                                <SelectTrigger className="h-8 text-xs">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white z-50 shadow-lg">
                                    {OPERATORS.map((op) => (
                                        <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Value */}
                    <div className="mt-2">
                        <div className="flex items-center justify-between mb-0.5">
                            <Label className="text-xs text-gray-600">Value</Label>
                            <AIGenerateButton
                                contentType="comparison value"
                                placeholder={`e.g. What value should ${cond.column || 'the column'} be compared to?`}
                                onGenerate={(text) => updateCondition(idx, 'value', text)}
                                context={
                                    (colNames.length > 0 ? `Available columns: ${colNames.join(', ')}\n` : '') +
                                    `Column: ${cond.column || 'unknown'}, Operator: ${cond.operator || '=='}\n` +
                                    'CRITICAL RULE: Return ONLY the value string (no quotes, no SQL keywords).'
                                }
                            />
                        </div>
                        <Input
                            value={cond.value || ''}
                            onChange={(e) => updateCondition(idx, 'value', e.target.value)}
                            placeholder="value"
                            className="h-8 text-xs"
                        />
                    </div>

                    {/* Preview */}
                    {cond.column && cond.operator && cond.value && (
                        <div className="mt-1 px-2 py-1 bg-white border border-orange-200 rounded text-[10px] font-mono text-gray-600">
                            WHERE "{cond.column}" {cond.operator} '{cond.value}'
                        </div>
                    )}
                </div>
            ))}

            {conditions.length === 0 && (
                <div className="text-center py-6 text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg">
                    No branches yet. Click "+ Add Branch" to define a condition.
                </div>
            )}

            <div className="p-2 bg-gray-50 border border-gray-200 rounded-lg flex items-center gap-2">
                <div className="w-3 h-3 rounded-full border-2 border-gray-400 flex-shrink-0" />
                <span className="text-xs text-gray-600 font-medium">Default</span>
                <span className="text-[10px] text-gray-400">-- catches all rows that don't match any condition above</span>
            </div>
        </div>
    );
};

// 5. Set Variable Configuration
export const SetVariableConfig = ({ data, onChange, inputColumns = [], flowVariables = [] }: BaseConfigProps & { flowVariables?: { name: string, type: string, value: any }[] }) => {
    const colNames = (Array.isArray(inputColumns) ? inputColumns : [])
        .map((c: any) => (typeof c === 'string' ? c : c?.name || c)).filter(Boolean);
    const fvars = Array.isArray(flowVariables) ? flowVariables : [];

    return (
        <div className="space-y-4">
            {/* Standard Header */}
            <div className="bg-violet-50 p-3 rounded-lg border border-violet-100 flex items-start gap-3">
                <div className="mt-0.5 bg-violet-100 p-1.5 rounded text-violet-600 shrink-0">
                    <Braces className="h-4 w-4" />
                </div>
                <div>
                    <h3 className="text-xs font-semibold text-violet-900 mb-0.5">Store a value</h3>
                    <p className="text-[10px] text-violet-700 leading-relaxed">
                        Saves a named value into the flow's context. Any downstream <strong>Get Variable</strong> node can read it back.
                        This node passes its input data through unchanged.
                    </p>
                </div>
            </div>

            {/* Variable Name */}
            <div>
                <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Variable Name</Label>
                {fvars.length > 0 ? (
                    <>
                        {/* Pill picker of flow variables */}
                        <div className="flex flex-wrap gap-1.5 mt-2 mb-1">
                            {fvars.map(v => (
                                <button
                                    key={v.name}
                                    type="button"
                                    onClick={() => {
                                        onChange('variableName', v.name);
                                        // Pre-fill static value from flow variable's initial value
                                        if ((data.valueSource || 'static') === 'static' && !data.variableValue) {
                                            onChange('variableValue', String(v.value ?? ''));
                                        }
                                    }}
                                    className={`px-2.5 py-1 text-[11px] font-mono rounded-full border transition-all ${data.variableName === v.name
                                        ? 'bg-violet-600 text-white border-violet-600 shadow-sm'
                                        : 'bg-white text-violet-700 border-violet-200 hover:bg-violet-50'
                                        }`}
                                >
                                    {v.name}
                                    <span className="ml-1 text-[9px] opacity-60">{v.type}</span>
                                </button>
                            ))}
                        </div>
                        <Input
                            className="mt-1 font-mono text-xs"
                            value={data.variableName || ''}
                            onChange={(e) => onChange('variableName', e.target.value)}
                            placeholder="or type a new name..."
                        />
                    </>
                ) : (
                    <Input
                        className="mt-1 font-mono text-xs"
                        value={data.variableName || ''}
                        onChange={(e) => onChange('variableName', e.target.value)}
                        placeholder="e.g. invoice_total"
                    />
                )}
                <p className="text-[10px] text-gray-400 mt-1">
                    {fvars.length > 0
                        ? 'Click a flow variable pill above, or type a custom name.'
                        : 'Use a simple identifier with no spaces (letters, numbers, underscores).'}
                </p>
            </div>

            {/* Value source toggle */}
            <div>
                <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Value Source</Label>
                <div className="flex gap-2 mt-1">
                    {[{ v: 'static', label: 'Static Value' }, { v: 'column', label: 'From Column' }].map(opt => (
                        <button
                            key={opt.v}
                            onClick={() => onChange('valueSource', opt.v)}
                            className={`flex-1 py-1.5 text-xs font-semibold rounded-lg border transition-all ${(data.valueSource || 'static') === opt.v
                                ? 'bg-violet-600 text-white border-violet-600 shadow-sm'
                                : 'bg-white text-gray-600 border-gray-200 hover:border-violet-300'
                                }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Static value input */}
            {(data.valueSource || 'static') === 'static' && (
                <div>
                    <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Value</Label>
                    <Input
                        className="mt-1 text-xs"
                        value={data.variableValue || ''}
                        onChange={(e) => onChange('variableValue', e.target.value)}
                        placeholder="e.g. 1000 or hello"
                    />
                    {/* Show current value from flow variable */}
                    {(() => {
                        const v = fvars.find(f => f.name === data.variableName);
                        return v ? (
                            <p className="text-[10px] text-violet-500 mt-1">
                                Flow variable default: <code className="bg-violet-50 px-1 rounded">{String(v.value)}</code>
                            </p>
                        ) : null;
                    })()}
                </div>
            )}

            {/* Column picker */}
            {(data.valueSource) === 'column' && (
                <div>
                    <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Source Column</Label>
                    {colNames.length > 0 ? (
                        <Select value={data.sourceColumn || ''} onValueChange={(v) => onChange('sourceColumn', v)}>
                            <SelectTrigger className="mt-1 h-8 text-xs">
                                <SelectValue placeholder="Select column..." />
                            </SelectTrigger>
                            <SelectContent className="max-h-[200px] bg-white z-50 shadow-lg">
                                {colNames.map((col: string) => (
                                    <SelectItem key={col} value={col}>{col}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <Input
                            className="mt-1 text-xs font-mono"
                            value={data.sourceColumn || ''}
                            onChange={(e) => onChange('sourceColumn', e.target.value)}
                            placeholder="column_name"
                        />
                    )}
                    <p className="text-[10px] text-gray-400 mt-1">Uses the first row's value from this column.</p>
                </div>
            )}
        </div>
    );
};

// 6. Get Variable Configuration
export const GetVariableConfig = ({ data, onChange, flowVariables = [] }: BaseConfigProps & { flowVariables?: { name: string, type: string, value: any }[] }) => {
    const fvars = Array.isArray(flowVariables) ? flowVariables : [];
    const selectedVar = fvars.find(v => v.name === data.variableName);

    return (
        <div className="space-y-4">
            {/* Standard Header */}
            <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-100 flex items-start gap-3">
                <div className="mt-0.5 bg-indigo-100 p-1.5 rounded text-indigo-600 shrink-0">
                    <Braces className="h-4 w-4" />
                </div>
                <div>
                    <h3 className="text-xs font-semibold text-indigo-900 mb-0.5">Read a value</h3>
                    <p className="text-[10px] text-indigo-700 leading-relaxed">
                        Reads a named variable from the flow's context and adds it as a new column to every row.
                        A <strong>Set Variable</strong>node must run earlier in the flow (or define it in<strong>Flow Variables</strong>).
                    </p>
                </div>
            </div>

            {/* Variable Name -- pill picker from Flow Variables */}
            <div>
                <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Variable Name</Label>
                {fvars.length > 0 ? (
                    <>
                        <div className="flex flex-wrap gap-1.5 mt-2 mb-2">
                            {fvars.map(v => (
                                <button
                                    key={v.name}
                                    type="button"
                                    onClick={() => {
                                        onChange('variableName', v.name);
                                        // Auto-fill output column name if empty
                                        if (!data.outputColumn) {
                                            onChange('outputColumn', v.name);
                                        }
                                    }}
                                    title={`Type: ${v.type} | Default: ${v.value}`}
                                    className={`px-2.5 py-1 text-[11px] font-mono rounded-full border transition-all ${data.variableName === v.name
                                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                                        : 'bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50'
                                        }`}
                                >
                                    {v.name}
                                    <span className="ml-1 text-[9px] opacity-60">{v.type}</span>
                                </button>
                            ))}
                        </div>
                        <Input
                            className="font-mono text-xs"
                            value={data.variableName || ''}
                            onChange={(e) => onChange('variableName', e.target.value)}
                            placeholder="or type name from Set Variable node..."
                        />
                    </>
                ) : (
                    <>
                        <Input
                            className="mt-1 font-mono text-xs"
                            value={data.variableName || ''}
                            onChange={(e) => onChange('variableName', e.target.value)}
                            placeholder="e.g. invoice_total"
                        />
                        <p className="text-[10px] text-gray-400 mt-1">
                            No Flow Variables defined yet. Open the <strong>Flow Variables</strong> panel to add some, or type the name from a Set Variable node.
                        </p>
                    </>
                )}
                {selectedVar && (
                    <div className="mt-1.5 px-2 py-1.5 bg-indigo-50 border border-indigo-100 rounded text-[10px] text-indigo-700 flex items-center gap-2">
                        <span className="font-semibold">{selectedVar.name}</span>
                        <span className="text-indigo-400">.</span>
                        <span>{selectedVar.type}</span>
                        <span className="text-indigo-400">.</span>
                        <span>default:<code className="bg-white px-1 rounded">{String(selectedVar.value)}</code></span>
                    </div>
                )}
                {!selectedVar && fvars.length > 0 && data.variableName && (
                    <p className="text-[10px] text-amber-600 mt-1">[!] Not in Flow Variables -- ensure a Set Variable node sets this name.</p>
                )}
            </div>

            {/* Output Column */}
            <div>
                <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Output Column Name</Label>
                <Input
                    className="mt-1 font-mono text-xs"
                    value={data.outputColumn || ''}
                    onChange={(e) => onChange('outputColumn', e.target.value)}
                    placeholder={data.variableName || 'my_variable'}
                />
                <p className="text-[10px] text-gray-400 mt-1">The new column added to each row. Defaults to the variable name.</p>
            </div>

            {/* Optional Default */}
            <div>
                <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Default Value<span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input
                    className="mt-1 text-xs"
                    value={data.defaultValue || ''}
                    onChange={(e) => onChange('defaultValue', e.target.value)}
                    placeholder="Fallback if variable not set"
                />
                <p className="text-[10px] text-gray-400 mt-1">If left empty and the variable isn't set, the node will raise an error.</p>
            </div>
        </div>
    );
};

// 7. Data Mapper Configuration
export const DataMapperConfig = ({ data, onChange, inputColumns = [] }: BaseConfigProps) => {
    useEffect(() => {
        console.log("[DataMapperConfig] Props received:", {
            data,
            inputColumnsCount: inputColumns?.length,
            inputColumnsSample: inputColumns?.slice(0, 2)
        });
    }, [data, inputColumns]);

    const colNames = (Array.isArray(inputColumns) ? inputColumns : [])
        .map((c: any) => (typeof c === 'string' ? c : c?.name || c)).filter(Boolean);
    const mappings = data.mappings || [];

    // AI States
    const [aiPrompts, setAiPrompts] = useState<Record<number, string>>({});
    const [isGenerating, setIsGenerating] = useState<Record<number, boolean>>({});

    const addMapping = () => {
        onChange('mappings', [
            ...mappings,
            { sourceColumn: '', targetColumn: '', transformation: '' }
        ]);
    };

    const removeMapping = (index: number) => {
        onChange('mappings', mappings.filter((_: any, i: number) => i !== index));
    };

    const updateMapping = (index: number, field: string, value: string) => {
        const updated = [...mappings];
        updated[index] = { ...updated[index], [field]: value };
        onChange('mappings', updated);
    };

    const handleGenerateExpression = async (index: number) => {
        const prompt = aiPrompts[index];
        if (!prompt || !prompt.trim()) return;

        setIsGenerating(prev => ({ ...prev, [index]: true }));
        try {
            const res = await apiFetch('/ai/transform', null, {
                method: 'POST',
                body: JSON.stringify({
                    Prompt: prompt,
                    Columns: inputColumns.map((c: any) => ({
                        Name: typeof c === 'string' ? c : c.name || '',
                        DataType: typeof c === 'string' ? 'string' : c.type || c.dataType || 'string'
                    })),
                    TaskType: 'Formula'
                })
            });

            console.log("[DataMapperConfig] AI Response:", res);


            if (res?.expression || res?.Expression) {
                let expr = res.expression || res.Expression;

                // Strip double quotes if the AI wrapped the entire SQL expression in them
                if (typeof expr === 'string' && expr.startsWith('"') && expr.endsWith('"') && expr.length >= 2) {
                    expr = expr.substring(1, expr.length - 1);
                }

                updateMapping(index, 'transformation', expr);
                // Clear prompt on success
                setAiPrompts(prev => ({ ...prev, [index]: '' }));
            }
        } catch (e) {
            console.error("AI Generation Failed", e);
            toast.error("AI Generation Failed. Please try again.");
        } finally {
            setIsGenerating(prev => ({ ...prev, [index]: false }));
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <Label>Column Mappings</Label>
                <Button size="sm" onClick={addMapping}>
                    <Plus className="h-4 w-4 mr-1" /> Add Mapping
                </Button>
            </div>

            {mappings.map((mapping: any, idx: number) => (
                <div key={idx} className="border p-3 rounded-md space-y-2 bg-gray-50/30">
                    <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Mapping {idx + 1}</span>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeMapping(idx)}
                        >
                            <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-[10px] uppercase text-gray-500">Source Column</Label>
                            {colNames.length > 0 ? (
                                <Select value={mapping.sourceColumn || ''} onValueChange={(v) => updateMapping(idx, 'sourceColumn', v)}>
                                    <SelectTrigger className="w-full bg-white border-gray-300 h-8 text-xs">
                                        <SelectValue placeholder="Select source" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white border-gray-300 shadow-lg">
                                        {colNames.map((col: string) => (
                                            <SelectItem key={col} value={col}>{col}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <Input
                                    className="h-8 text-xs bg-white"
                                    value={mapping.sourceColumn || ''}
                                    onChange={(e) => updateMapping(idx, 'sourceColumn', e.target.value)}
                                    placeholder="original_column"
                                />
                            )}
                        </div>

                        <div>
                            <Label className="text-[10px] uppercase text-gray-500">Target Column</Label>
                            <Input
                                className="h-8 text-xs bg-white"
                                value={mapping.targetColumn || ''}
                                onChange={(e) => updateMapping(idx, 'targetColumn', e.target.value)}
                                placeholder="new_column"
                            />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <Label className="text-[10px] uppercase text-gray-500">Transformation</Label>

                        {/* AI Input Area */}
                        <div className="flex gap-1 items-center mb-1.5 bg-purple-50/50 p-1.5 rounded border border-purple-100/50">
                            <Wand2 className="h-3 w-3 text-purple-500 shrink-0" />
                            <input
                                className="flex-1 text-[10px] bg-transparent border-none focus:outline-none text-purple-900 placeholder:text-purple-300"
                                placeholder="AI generate: e.g. add 30 days to date..."
                                value={aiPrompts[idx] || ''}
                                onChange={(e) => setAiPrompts(prev => ({ ...prev, [idx]: e.target.value }))}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleGenerateExpression(idx);
                                    }
                                }}
                            />
                            <button
                                type="button"
                                className="text-[9px] bg-purple-600 text-white px-1.5 py-0.5 rounded shadow-sm hover:bg-purple-700 disabled:opacity-50 transition-colors"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleGenerateExpression(idx);
                                }}
                                disabled={isGenerating[idx] || !aiPrompts[idx]?.trim()}
                            >
                                {isGenerating[idx] ? '...' : <Sparkles className="h-3 w-3" />}
                            </button>
                        </div>

                        <Input
                            className="h-8 text-xs bg-white font-mono"
                            value={mapping.transformation || ''}
                            onChange={(e) => updateMapping(idx, 'transformation', e.target.value)}
                            placeholder="e.g. UPPER(source_column)"
                        />
                        <p className="text-[9px] text-gray-400 italic">Use AI above to generate logic or write manually.</p>
                    </div>
                </div>
            ))}
        </div>
    );
};

// 6. Document Generator Configuration -- Format selector, AI converts upstream output to selected format
export const DocumentGeneratorConfig = ({ data, onChange, inputColumns = [] }: BaseConfigProps) => {
    const colNames = (Array.isArray(inputColumns) ? inputColumns : [])
        .map((c: any) => (typeof c === 'string' ? c : c?.name || c)).filter(Boolean);
    const useAiFormat = data.useAiFormat !== false;
    const outputFormat = data.outputFormat || 'docx';
    return (
        <div className="space-y-4">
            <div>
                <Label>Output Format</Label>
                <Select
                    value={outputFormat}
                    onValueChange={(value) => onChange('outputFormat', value)}
                >
                    <SelectTrigger className="w-full bg-white border-gray-300">
                        <SelectValue placeholder="Select format" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-300 shadow-lg">
                        <SelectItem value="docx">DOCX</SelectItem>
                        <SelectItem value="html">HTML</SelectItem>
                        <SelectItem value="pdf">PDF</SelectItem>
                        <SelectItem value="markdown">Markdown</SelectItem>
                        <SelectItem value="json">JSON</SelectItem>
                    </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Upstream data will be converted to this format</p>
            </div>
            <div className="flex items-center justify-between">
                <div>
                    <Label>Use AI to format content</Label>
                    <p className="text-xs text-gray-500">AI converts each row from upstream into the selected format</p>
                </div>
                <Switch
                    checked={useAiFormat}
                    onCheckedChange={(v) => onChange('useAiFormat', v)}
                />
            </div>
            {!useAiFormat && (
                <div>
                    <Label>Template Path (optional)</Label>
                    <Input
                        value={data.templatePath || ''}
                        onChange={(e) => onChange('templatePath', e.target.value)}
                        placeholder="/path/to/template.docx"
                    />
                    <p className="text-xs text-gray-500 mt-1">Use {'{{field_name}}'} placeholders</p>
                </div>
            )}
            <div>
                <Label>Output Path</Label>
                <Input
                    value={data.outputPath || '/tmp/generated_docs'}
                    onChange={(e) => onChange('outputPath', e.target.value)}
                    placeholder="/path/to/output"
                />
            </div>
            {colNames.length > 0 && (
                <div className="bg-gray-50 p-2 rounded text-[10px] text-gray-600 border">
                    <span className="font-medium">Upstream columns:</span>
                    {colNames.join(', ')}
                </div>
            )}
        </div>
    );
};

// 7. Duplicate Detector Configuration (dynamic input from upstream or DB connection)
const DB_TYPES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 13];
const DB_TYPE_NAMES: Record<number, string> = {
    0: 'PostgreSQL', 1: 'MySQL', 2: 'SQL Server', 3: 'Oracle',
    4: 'Snowflake', 5: 'BigQuery', 6: 'Redshift', 7: 'Azure SQL', 8: 'Synapse', 13: 'DuckDB'
};

export const DuplicateDetectorConfig = ({ data, onChange, inputColumns = [], flowId, nodeId }: BaseConfigProps & { flowId?: string; nodeId?: string }) => {
    const upstreamCols = (Array.isArray(inputColumns) ? inputColumns : [])
        .map((c: any) => (typeof c === 'string' ? c : c?.name || c)).filter(Boolean);
    const [connections, setConnections] = useState<any[]>([]);
    const [dbTables, setDbTables] = useState<string[]>([]);
    const [dbColumns, setDbColumns] = useState<string[]>([]);
    const [loadingTables, setLoadingTables] = useState(false);
    const [loadingCols, setLoadingCols] = useState(false);
    const dataSource = data.dataSource || (upstreamCols.length > 0 ? 'upstream' : 'database');
    const connId = data.connectionId || '';
    const dbTable = data.dbTable || '';

    useEffect(() => {
        fetchDatasources().then(d => setConnections(d));
    }, []);
    useEffect(() => {
        if (!connId) { setDbTables([]); setDbColumns([]); return; }
        setLoadingTables(true);
        apiFetch(`/datasources/${connId}/structure`, null, { method: 'GET' })
            .then((d: any) => { setDbTables((d?.tables || []).map((t: any) => t.name || t)); })
            .catch(() => setDbTables([]))
            .finally(() => setLoadingTables(false));
    }, [connId]);
    useEffect(() => {
        if (!connId || !dbTable) { setDbColumns([]); return; }
        setLoadingCols(true);
        apiFetch(`/datasources/${connId}/structure`, null, { method: 'GET' })
            .then((d: any) => {
                const tbl = (d?.tables || []).find((t: any) => (t.name || t) === dbTable);
                setDbColumns(tbl?.columns?.map((c: any) => c.name || c) || []);
            })
            .catch(() => setDbColumns([]))
            .finally(() => setLoadingCols(false));
    }, [connId, dbTable]);

    const colNames = dataSource === 'upstream' ? upstreamCols : dbColumns;

    const keyCols: string[] = Array.isArray(data.keyColumns) ? data.keyColumns
        : (typeof data.keyColumns === 'string' ? data.keyColumns.split(',').map((s: string) => s.trim()).filter(Boolean) : []);
    const sortCols: string[] = Array.isArray(data.sortColumns) ? data.sortColumns
        : (typeof data.sortColumns === 'string' ? data.sortColumns.split(',').map((s: string) => s.trim()).filter(Boolean) : []);
    const blockCols: string[] = Array.isArray(data.blockingKeys) ? data.blockingKeys
        : (typeof data.blockingKeys === 'string' ? data.blockingKeys.split(',').map((s: string) => s.trim()).filter(Boolean) : []);

    const toggleCol = (list: string[], col: string, key: string) => {
        const next = list.includes(col) ? list.filter(c => c !== col) : [...list, col];
        onChange(key, next);
    };

    const strategy = data.strategy || 'exact';
    const [testResult, setTestResult] = useState<{ totalRows?: number; uniqueCount?: number; duplicateCount?: number } | null>(null);
    const [testLoading, setTestLoading] = useState(false);

    const sampleCols = keyCols.length ? keyCols : (colNames.length ? colNames.slice(0, 2) : ['id', 'name']);
    const buildSample = () => sampleCols.length ? [
        Object.fromEntries(sampleCols.map((c, i) => [c, i === 0 ? 'A' : '1'])),
        Object.fromEntries(sampleCols.map((c, i) => [c, i === 0 ? 'A' : '2'])),
        Object.fromEntries(sampleCols.map((c, i) => [c, i === 0 ? 'B' : '3'])),
    ] : [{ id: '1', name: 'a' }, { id: '2', name: 'a' }, { id: '3', name: 'b' }];

    const handleTestDedup = async () => {
        setTestLoading(true);
        setTestResult(null);
        try {
            const res = await fetch(`${API_BASE_URL}/flows/ops/duplicate-detector/test`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
                body: JSON.stringify({
                    flowId: flowId || 'test',
                    nodeId: nodeId || 'test-dedup',
                    config: { ...data, keyColumns: keyCols.length ? keyCols : sampleCols },
                    sampleData: buildSample(),
                }),
            });
            const json = await res.json();
            if (json.success) setTestResult(json);
            else setTestResult({ duplicateCount: -1 });
        } catch {
            setTestResult({ duplicateCount: -1 });
        } finally {
            setTestLoading(false);
        }
    };

    return (
        <div className="space-y-4">
            <div>
                <Label>Data Source (for column selection)</Label>
                <Select value={dataSource} onValueChange={(v) => { onChange('dataSource', v); if (v === 'upstream') onChange('connectionId', ''); }}>
                    <SelectTrigger className="w-full bg-white border-gray-300">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-300 shadow-lg">
                        <SelectItem value="upstream">From upstream (connected node output)</SelectItem>
                        <SelectItem value="database">From database (connection + table)</SelectItem>
                    </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Use upstream when connected; use database to preview columns before connecting.</p>
            </div>
            {dataSource === 'database' && (
                <>
                    <div>
                        <Label>Database Connection</Label>
                        <select
                            className="w-full mt-1 text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                            value={connId}
                            onChange={(e) => { onChange('connectionId', e.target.value); onChange('dbTable', ''); }}
                        >
                            <option value="">Select connection...</option>
                            {(connections.filter((c: any) => DB_TYPES.includes(c.type))).map((c: any) => (
                                <option key={c.id} value={String(c.id)}>{c.name} ({DB_TYPE_NAMES[c.type] || 'DB'})</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label>Table</Label>
                        <select
                            className="w-full mt-1 text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                            value={dbTable}
                            onChange={(e) => onChange('dbTable', e.target.value)}
                            disabled={!connId || loadingTables}
                        >
                            <option value="">{connId ? (loadingTables ? 'Loading...' : 'Select table...') : 'Select connection first'}</option>
                            {dbTables.map((t: string) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                </>
            )}
            <div>
                <Label>Strategy</Label>
                <Select value={strategy} onValueChange={(v) => onChange('strategy', v)}>
                    <SelectTrigger className="w-full bg-white border-gray-300">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-300 shadow-lg">
                        <SelectItem value="exact">Exact Match</SelectItem>
                        <SelectItem value="fuzzy">Fuzzy (Levenshtein/Similarity)</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            {strategy === 'fuzzy' && (
                <div>
                    <Label>Similarity Threshold (0.0-1.0)</Label>
                    <Input
                        type="number"
                        min={0}
                        max={1}
                        step={0.05}
                        value={data.similarityThreshold ?? 0.85}
                        onChange={(e) => onChange('similarityThreshold', parseFloat(e.target.value) || 0.85)}
                        placeholder="0.85"
                    />
                    <p className="text-xs text-gray-500 mt-1">Rows with similarity &gt;= this are treated as duplicates</p>
                </div>
            )}
            <div>
                <Label>Key Columns</Label>
                {colNames.length > 0 ? (
                    <div className="border border-gray-200 rounded-lg bg-gray-50 p-2 max-h-36 overflow-y-auto space-y-1 mt-1">
                        {colNames.map((col: string) => (
                            <label key={col} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-white rounded px-1 py-0.5">
                                <input type="checkbox" checked={keyCols.includes(col)} onChange={() => toggleCol(keyCols, col, 'keyColumns')}
                                    className="rounded text-blue-600 h-3.5 w-3.5" />
                                <span className="font-mono text-gray-700">{col}</span>
                            </label>
                        ))}
                    </div>
                ) : (
                    <Input
                        value={keyCols.join(', ')}
                        onChange={(e) => onChange('keyColumns', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                        placeholder={dataSource === 'database' && !connId ? 'Select connection and table first' : 'column1, column2, column3'}
                    />
                )}
                <p className="text-xs text-gray-500 mt-1">
                    {colNames.length > 0 ? 'Select columns to check for duplicates. None = all columns.' : dataSource === 'database' && (!connId || !dbTable) ? 'Select connection and table to load columns.' : 'Comma-separated column names. Leave empty to use all columns.'}
                </p>
            </div>

            {colNames.length > 0 && (
                <div>
                    <Label>Blocking Keys (optional)</Label>
                    <div className="border border-gray-200 rounded-lg bg-gray-50 p-2 max-h-28 overflow-y-auto flex flex-wrap gap-2 mt-1">
                        {colNames.map((col: string) => (
                            <label key={col} className="flex items-center gap-1.5 text-xs cursor-pointer">
                                <input type="checkbox" checked={blockCols.includes(col)} onChange={() => toggleCol(blockCols, col, 'blockingKeys')} className="rounded" />
                                <span className="font-mono">{col}</span>
                            </label>
                        ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Pre-filter: compare only rows with same blocking key values</p>
                </div>
            )}
            <div>
                <Label>Action</Label>
                <Select
                    value={data.action || 'remove'}
                    onValueChange={(value) => onChange('action', value)}
                >
                    <SelectTrigger className="w-full bg-white border-gray-300">
                        <SelectValue placeholder="Select action" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-300 shadow-lg">
                        <SelectItem value="remove">Remove Duplicates</SelectItem>
                        <SelectItem value="mark">Mark Duplicates</SelectItem>
                        <SelectItem value="report">Report Only</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            {data.action === 'remove' && (
                <div>
                    <Label>Output Mode</Label>
                    <Select value={data.outputMode || 'deduplicated'} onValueChange={(v) => onChange('outputMode', v)}>
                        <SelectTrigger className="w-full bg-white border-gray-300">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-300 shadow-lg">
                            <SelectItem value="deduplicated">Deduplicated set (keep unique)</SelectItem>
                            <SelectItem value="duplicates_only">Duplicates only (for review)</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            )}

            <div className="flex items-center justify-between border-t pt-3">
                <Label>Test / Preview</Label>
                <Button size="sm" variant="outline" onClick={handleTestDedup} disabled={testLoading}>
                    {testLoading ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Testing...</> : <><Search className="h-3 w-3 mr-1" />Test</>}
                </Button>
            </div>
            {testResult && (
                <div className="text-xs bg-gray-50 border rounded p-2">
                    {testResult.duplicateCount === -1 ? (
                        <span className="text-red-600">Test failed</span>
                    ) : (
                        <>Total: {testResult.totalRows} | Unique: {testResult.uniqueCount} | Duplicates: {testResult.duplicateCount}</>
                    )}
                </div>
            )}

            {data.action !== 'report' && (
                <>
                    <div>
                        <Label>Sort Columns (for keep first/last)</Label>
                        {colNames.length > 0 ? (
                            <div className="border border-gray-200 rounded-lg bg-gray-50 p-2 max-h-36 overflow-y-auto space-y-1 mt-1">
                                {colNames.map((col: string) => (
                                    <label key={col} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-white rounded px-1 py-0.5">
                                        <input type="checkbox" checked={sortCols.includes(col)} onChange={() => toggleCol(sortCols, col, 'sortColumns')}
                                            className="rounded text-blue-600 h-3.5 w-3.5" />
                                        <span className="font-mono text-gray-700">{col}</span>
                                    </label>
                                ))}
                            </div>
                        ) : (
                            <Input
                                value={sortCols.join(', ')}
                                onChange={(e) => onChange('sortColumns', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                placeholder="column1, column2 (determines order)"
                            />
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                            Optional. Columns to order by when deciding which row to keep.
                        </p>
                    </div>
                    <div>
                        <Label>Keep</Label>
                        <Select
                            value={data.keepOrder || 'first'}
                            onValueChange={(value) => onChange('keepOrder', value)}
                        >
                            <SelectTrigger className="w-full bg-white border-gray-300">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-300 shadow-lg">
                                <SelectItem value="first">First occurrence</SelectItem>
                                <SelectItem value="last">Last occurrence</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </>
            )}
        </div>
    );
};

// ── Flow Assets Panel ─────────────────────────────────────────────────────────
// Supports any file type: .py (auto-importable), .pkl, .csv, .json, etc.
// All files are stored in /tmp/flow_libs/{tenant_id}/{flow_id}/ and
// accessible in scripts as: open(f"{__lib_dir__}/model.pkl", "rb")

const TEXT_EXTS = new Set(['.py', '.csv', '.json', '.yaml', '.yml', '.txt', '.md', '.toml', '.ini', '.sql', '.xml']);
const EXT_LANG: Record<string, string> = {
    '.py': 'python', '.json': 'json', '.yaml': 'yaml', '.yml': 'yaml',
    '.csv': 'plaintext', '.txt': 'plaintext', '.md': 'markdown',
    '.sql': 'sql', '.xml': 'xml', '.toml': 'plaintext', '.ini': 'plaintext',
};

interface PyLib { name: string; size: number; updatedAt: number; viewable?: boolean; }

const PyLibsUploadSection = ({ flowId, onLibsChange }: { flowId?: string; onLibsChange?: (names: string[]) => void }) => {
    const [libs, setLibs] = useState<PyLib[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // viewer state
    const [viewFile, setViewFile] = useState<{ name: string; content: string | null; binary: boolean; size?: number } | null>(null);
    const [viewLoading, setViewLoading] = useState(false);

    const fetchLibs = async () => {
        if (!flowId || flowId === 'new') return;
        setLoading(true);
        setError(null);
        try {
            const d = await apiFetch(`/flows/${flowId}/python-libs`, null, { method: 'GET' });
            const files: PyLib[] = d?.files || [];
            setLibs(files);
            // only .py files are importable as modules
            onLibsChange?.(files.filter(f => f.name.endsWith('.py')).map(f => f.name.replace('.py', '')));
        } catch (e: any) {
            setError(e?.message || 'Failed to load assets');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchLibs(); }, [flowId]);

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (!files.length || !flowId) return;
        setUploading(true);
        setError(null);
        try {
            const form = new FormData();
            files.forEach(f => form.append('files', f));
            await apiFetch(`/flows/${flowId}/python-libs`, null, { method: 'POST', body: form });
            await fetchLibs();
        } catch (e: any) {
            setError(e?.message || 'Upload failed');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDelete = async (filename: string) => {
        if (!flowId) return;
        setError(null);
        try {
            await apiFetch(`/flows/${flowId}/python-libs/${filename}`, null, { method: 'DELETE' });
            const next = libs.filter(l => l.name !== filename);
            setLibs(next);
            onLibsChange?.(next.filter(f => f.name.endsWith('.py')).map(f => f.name.replace('.py', '')));
        } catch (e: any) {
            setError(e?.message || 'Delete failed');
        }
    };

    const handleView = async (filename: string) => {
        if (!flowId) return;
        setViewLoading(true);
        setViewFile(null);
        try {
            const d = await apiFetch(`/flows/${flowId}/python-libs/${filename}/content`, null, { method: 'GET' });
            setViewFile({ name: d.filename, content: d.content, binary: d.binary ?? false, size: d.size });
        } catch (e: any) {
            setError(e?.message || 'Failed to load file');
        } finally {
            setViewLoading(false);
        }
    };

    const fmtSize = (b: number) => b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`;
    const extOf = (name: string) => '.' + name.split('.').pop()!.toLowerCase();
    const pyLibs = libs.filter(l => l.name.endsWith('.py'));

    return (
        <>
            <div className="border border-indigo-100 rounded-lg bg-indigo-50/50 p-3 space-y-2">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold text-indigo-800">Flow Assets</span>
                        <span className="text-[10px] text-indigo-400">(.py, .pkl, .csv, .json ...)</span>
                    </div>
                    <div className="flex items-center gap-1">
                        {loading && <Loader2 className="h-3 w-3 animate-spin text-indigo-400" />}
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading || !flowId || flowId === 'new'}
                            className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-100 transition-colors disabled:opacity-50"
                        >
                            {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                            Upload
                        </button>
                        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleUpload} />
                    </div>
                </div>

                {error && <p className="text-[10px] text-red-500">{error}</p>}

                {(!flowId || flowId === 'new') && (
                    <p className="text-[10px] text-amber-600 italic">Save the flow first to enable asset uploads.</p>
                )}

                {libs.length === 0 && flowId && flowId !== 'new' && !loading && !error && (
                    <p className="text-[10px] text-indigo-400 italic">No assets yet. Upload .py, .pkl, .csv, .json, .yaml or any other file.</p>
                )}

                {/* File list */}
                {libs.length > 0 && (
                    <div className="space-y-1">
                        {libs.map(lib => {
                            const ext = extOf(lib.name);
                            const isViewable = lib.viewable ?? TEXT_EXTS.has(ext);
                            const isPy = lib.name.endsWith('.py');
                            return (
                                <div key={lib.name} className="flex items-center justify-between bg-white rounded px-2 py-1 border border-indigo-100 gap-1">
                                    <div className="flex items-center gap-1.5 min-w-0">
                                        <span className={`text-[10px] px-1 rounded font-mono shrink-0 ${isPy ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                                            {ext}
                                        </span>
                                        <span className="text-xs font-mono text-gray-800 truncate">{lib.name}</span>
                                        <span className="text-[10px] text-gray-400 shrink-0">{fmtSize(lib.size)}</span>
                                    </div>
                                    <div className="flex items-center gap-0.5 shrink-0">
                                        {isViewable && (
                                            <button
                                                type="button"
                                                onClick={() => handleView(lib.name)}
                                                className="text-indigo-300 hover:text-indigo-600 transition-colors p-0.5"
                                                title="View file"
                                            >
                                                {viewLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => handleDelete(lib.name)}
                                            className="text-gray-300 hover:text-red-500 transition-colors p-0.5"
                                            title="Delete"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}

                        {/* import hint -- only for .py files */}
                        {pyLibs.length > 0 && (
                            <div className="mt-1.5 p-2 bg-[#1e1e1e] rounded text-[11px] font-mono text-[#4ec9b0]">
                                <span className="text-[#6796e6]">import</span>
                                {pyLibs.map(l => l.name.replace('.py', '')).join(', ')}
                            </div>
                        )}

                        {/* binary file access hint */}
                        {libs.some(l => !l.name.endsWith('.py')) && (
                            <div className="mt-1 p-2 bg-[#1e1e1e] rounded text-[11px] font-mono text-[#ce9178]">
                                <span className="text-[#9cdcfe]">import</span>pickle<br />
                                <span className="text-[#dcdcaa]">model</span> = pickle.load(
                                <span className="text-amber-300">open</span>(
                                <span className="text-[#6796e6]">f"</span>
                                <span className="text-[#4ec9b0]">{'{__lib_dir__}'}</span>
                                <span className="text-[#6796e6]">/model.pkl"</span>,<span className="text-[#ce9178]">"rb"</span>))
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ── File Viewer Modal ── */}
            <Dialog open={!!viewFile} onOpenChange={() => setViewFile(null)}>
                <DialogContent
                    className="max-w-[90vw] w-[90vw] h-[85vh] flex flex-col p-0 gap-0 overflow-hidden"
                    onKeyDown={(e) => e.stopPropagation()}
                >
                    {/* Modal header */}
                    <div className="flex items-center justify-between px-4 py-2.5 border-b bg-[#1e1e1e] shrink-0">
                        <div className="flex items-center gap-2">
                            <div className="flex gap-1.5">
                                <div className="w-3 h-3 rounded-full bg-red-500 opacity-80" />
                                <div className="w-3 h-3 rounded-full bg-yellow-400 opacity-80" />
                                <div className="w-3 h-3 rounded-full bg-green-500 opacity-80" />
                            </div>
                            <span className="text-gray-300 text-sm font-mono ml-2">{viewFile?.name}</span>
                            <span className="text-xs text-gray-500 ml-2">read-only - Flow Asset</span>
                        </div>
                        <button type="button" onClick={() => setViewFile(null)} className="p-1.5 rounded hover:bg-gray-700 text-gray-400 hover:text-gray-200">
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    {/* Content */}
                    {viewFile?.binary ? (
                        <div className="flex-1 flex flex-col items-center justify-center gap-3 bg-[#1e1e1e] text-center p-8">
                            <div className="text-5xl"></div>
                            <p className="text-gray-300 font-mono text-sm">{viewFile.name}</p>
                            <p className="text-gray-500 text-xs">{viewFile.size !== undefined ? fmtSize(viewFile.size) : ''} - Binary file -- cannot be displayed as text</p>
                            <div className="mt-4 p-3 bg-[#252526] rounded text-xs font-mono text-left text-[#ce9178] border border-[#333]">
                                <span className="text-gray-400"># Access in your Python script:</span><br />
                                <span className="text-[#9cdcfe]">import</span>pickle<br />
                                <span className="text-[#dcdcaa]">obj</span>= pickle.load(<span className="text-amber-300">open</span>(<span className="text-[#6796e6]">f"</span><span className="text-[#4ec9b0]">{'{__lib_dir__}'}</span><span className="text-[#6796e6]">/{viewFile.name}"</span>,<span className="text-[#ce9178]">"rb"</span>))
                            </div>
                        </div>
                    ) : (
                        <div className="flex-1 overflow-hidden">
                            <MonacoEditor
                                height="100%"
                                language={EXT_LANG[extOf(viewFile?.name || '')] || 'plaintext'}
                                value={viewFile?.content || ''}
                                options={{
                                    readOnly: true,
                                    domReadOnly: true,
                                    minimap: { enabled: false },
                                    fontSize: 13,
                                    lineNumbers: 'on',
                                    scrollBeyondLastLine: false,
                                    wordWrap: 'off',
                                    folding: true,
                                    renderLineHighlight: 'all',
                                }}
                                theme="vs-dark"
                            />
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};
// ────────────────────────────────────────────────────────────────────────────



// 8. Python Script Configuration
const PYTHON_EDITOR_DEFAULT = [
    '# Available variables:',
    '#   input_table   -&gt; name of upstream DuckDB table/view',
    '#   output_table  -&gt; name you MUST create (VIEW or TABLE)',
    '#   context       -&gt; use context.query(sql) or context.execute_query(sql)',
    '#   inputs        -&gt; dict {name: table_name} for all connected inputs',
    '#   pd, np, pa, pq -&gt; pandas, numpy, pyarrow, pyarrow.parquet',
    '',
    'import pandas as pd',
    'import pyarrow as pa',
    'import pyarrow.parquet as pq',
    '',
    'rows = context.query(f"SELECT * FROM {input_table}").fetchall()',
    'cols = [r[0] for r in context.query(f"DESCRIBE {input_table}").fetchall()]',
    'df = pd.DataFrame(rows, columns=cols)',
    '',
    '# --- your transformations here ---',
    '',
    'tbl = pa.Table.from_pandas(df, preserve_index=False)',
    'path = f"{context.temp_directory}/output.parquet"',
    'pq.write_table(tbl, path)',
    'context.execute_query(',
    '    f"CREATE OR REPLACE VIEW {output_table} AS SELECT * FROM read_parquet(\'{path}\')",',
    ')',
].join('\n');

const MONACO_OPTIONS_FULL = {
    language: 'python',
    theme: 'vs-dark',
    fontSize: 14,
    lineHeight: 22,
    minimap: { enabled: true },
    scrollBeyondLastLine: false,
    wordWrap: 'off' as const,
    tabSize: 4,
    insertSpaces: true,
    automaticLayout: true,
    formatOnPaste: true,
    folding: true,
    lineNumbers: 'on' as const,
    renderLineHighlight: 'line' as const,
    suggestOnTriggerCharacters: true,
    quickSuggestions: true,
    parameterHints: { enabled: true },
    padding: { top: 16, bottom: 16 },
    fontFamily: "'Fira Code', 'JetBrains Mono', 'Cascadia Code', Consolas, monospace",
    fontLigatures: true,
};

const MONACO_OPTIONS_MINI = {
    language: 'python',
    theme: 'vs-dark',
    fontSize: 12,
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    wordWrap: 'on' as const,
    tabSize: 4,
    insertSpaces: true,
    automaticLayout: true,
    lineNumbers: 'off' as const,
    folding: false,
    renderLineHighlight: 'none' as const,
    scrollbar: { vertical: 'hidden' as const, horizontal: 'hidden' as const },
    overviewRulerLanes: 0,
    hideCursorInOverviewRuler: true,
    padding: { top: 8, bottom: 8 },
    readOnly: true,
    domReadOnly: true,
};

export const PythonScriptConfig = ({ data, onChange, inputColumns = [], flowId }: BaseConfigProps) => {
    const [uploadedLibs, setUploadedLibs] = useState<string[]>([]);
    const ctx = [
        'Available: context (DuckDB), inputs, output_table, input_table, pd, np, pa, pq, variables (dict of flow variables).',
        uploadedLibs.length ? `Importable libs: ${uploadedLibs.join(', ')}.` : '',
        'Script must create output_table.',
    ].filter(Boolean).join(' ');
    const [editorOpen, setEditorOpen] = useState(false);
    const [draftScript, setDraftScript] = useState(data.script || '');
    const [copied, setCopied] = useState(false);

    const openEditor = () => {
        setDraftScript(data.script || '');
        setEditorOpen(true);
    };

    const applyScript = () => {
        onChange('script', draftScript);
        setEditorOpen(false);
    };

    const discardScript = () => {
        setDraftScript(data.script || '');
        setEditorOpen(false);
    };

    const copyScript = async () => {
        try {
            await navigator.clipboard.writeText(draftScript);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { }
    };

    return (
        <>
            <div className="space-y-4">
                <div>
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <Label>Python Script</Label>
                        <div className="flex items-center gap-1">
                            <AIGenerateButton
                                contentType="Python script"
                                placeholder="e.g. Filter data, transform columns. Use input_table, create output_table."
                                onGenerate={(text) => onChange('script', text)}
                                context={ctx}
                            />
                            <button
                                type="button"
                                onClick={openEditor}
                                title="Open Monaco editor"
                                className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
                            >
                                <Maximize2 className="h-3.5 w-3.5" />
                                Open Editor
                            </button>
                        </div>
                    </div>
                    {/* Mini Monaco preview (read-only) */}
                    <div className="rounded-lg overflow-hidden border border-gray-200" style={{ height: 180 }}>
                        <MonacoEditor
                            height="180px"
                            defaultLanguage="python"
                            value={data.script || PYTHON_EDITOR_DEFAULT}
                            options={MONACO_OPTIONS_MINI}
                            theme="vs-dark"
                            onChange={() => { }}
                        />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        Timeout: {data.timeoutSeconds ?? 60}s. Script must create <code className="bg-gray-100 px-1 rounded">output_table</code>. Click<strong>Open Editor</strong> to edit.
                    </p>
                </div>
                <div>
                    <Label>Timeout (seconds)</Label>
                    <Input
                        type="number"
                        min={5}
                        max={300}
                        value={data.timeoutSeconds ?? 60}
                        onChange={(e) => onChange('timeoutSeconds', parseInt(e.target.value) || 60)}
                    />
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    <p className="text-xs text-yellow-800">
                        <strong>Important:</strong>Create a view/table named<code className="bg-yellow-100 px-1 rounded">{'{output_table}'}</code> with your results.
                    </p>
                </div>
            </div>

            {/* ── Python Library Files ── */}
            <PyLibsUploadSection flowId={flowId} onLibsChange={setUploadedLibs} />

            {/* ── Fullscreen Monaco Editor Modal ── */}
            <Dialog open={editorOpen} onOpenChange={(open) => { if (!open) discardScript(); }}>
                <DialogContent
                    className="max-w-[95vw] w-[95vw] h-[92vh] flex flex-col p-0 gap-0 overflow-hidden"
                    style={{ maxHeight: '92vh' }}
                    onKeyDown={(e) => e.stopPropagation()}
                    onEscapeKeyDown={(e) => e.preventDefault()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-2.5 border-b bg-[#1e1e1e] shrink-0">
                        <div className="flex items-center gap-2">
                            {/* VS Code-style traffic lights */}
                            <div className="flex gap-1.5">
                                <div className="w-3 h-3 rounded-full bg-red-500 opacity-80" />
                                <div className="w-3 h-3 rounded-full bg-yellow-400 opacity-80" />
                                <div className="w-3 h-3 rounded-full bg-green-500 opacity-80" />
                            </div>
                            <span className="text-gray-300 text-sm font-mono ml-2">script.py</span>
                            <span className="text-xs text-gray-500 ml-3">Python - Flow Studio</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={copyScript}
                                className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border border-gray-600 bg-gray-700 hover:bg-gray-600 text-gray-200 transition-colors"
                            >
                                {copied ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
                                {copied ? 'Copied!' : 'Copy'}
                            </button>
                            <AIGenerateButton
                                contentType="Python script"
                                placeholder="e.g. Filter data, transform columns. Use input_table, create output_table."
                                onGenerate={(text) => setDraftScript(text)}
                                context={ctx}
                            />
                            <button
                                type="button"
                                onClick={discardScript}
                                className="p-1.5 rounded hover:bg-gray-700 text-gray-400 hover:text-gray-200"
                                title="Close without saving"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    {/* Available vars bar */}
                    <div className="px-4 py-1.5 bg-[#252526] text-[11px] font-mono shrink-0 border-b border-[#333] flex items-center gap-1 flex-wrap">
                        <span className="text-[#6796e6] mr-1">Available:</span>
                        {[
                            { name: 'input_table', color: '#4ec9b0' },
                            { name: 'output_table', color: '#4ec9b0' },
                            { name: 'context', color: '#dcdcaa' },
                            { name: 'inputs', color: '#9cdcfe' },
                            { name: 'variables', color: '#9cdcfe' },
                            { name: 'pd', color: '#ce9178' },
                            { name: 'np', color: '#ce9178' },
                            { name: 'pa', color: '#ce9178' },
                            { name: 'pq', color: '#ce9178' },
                        ].map(({ name, color }) => (
                            <span key={name} className="mr-2 px-1.5 py-0.5 rounded" style={{ color, background: '#1e1e1e' }}>{name}</span>
                        ))}
                        {uploadedLibs.length > 0 && (
                            <>
                                <span className="text-gray-500 mx-1">│</span>
                                <span className="text-[#6796e6] mr-1">import</span>
                                {uploadedLibs.map(name => (
                                    <span key={name} className="mr-2 px-1.5 py-0.5 rounded" style={{ color: '#c586c0', background: '#1e1e1e' }}>{name}</span>
                                ))}
                            </>
                        )}
                    </div>

                    {/* Monaco Editor */}
                    <div className="flex-1 overflow-hidden">
                        <MonacoEditor
                            height="100%"
                            defaultLanguage="python"
                            value={draftScript || PYTHON_EDITOR_DEFAULT}
                            onChange={(val) => setDraftScript(val ?? '')}
                            options={MONACO_OPTIONS_FULL}
                            theme="vs-dark"
                            onMount={(editor) => {
                                // Set empty-string to replace placeholder default on first open if script exists
                                if (draftScript) editor.focus();
                            }}
                        />
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between px-4 py-2.5 border-t bg-[#007acc] shrink-0">
                        <span className="text-xs text-white/80 font-mono">
                            Python - {draftScript.split('\n').length} lines - UTF-8
                        </span>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={discardScript}
                                className="border-white/30 text-white hover:bg-white/10 hover:text-white bg-transparent"
                            >
                                Discard
                            </Button>
                            <Button
                                size="sm"
                                onClick={applyScript}
                                className="bg-white text-[#007acc] hover:bg-white/90 font-semibold"
                            >
                                Save Script
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

// 9a.  Iterate Loop Configuration (LOOP node)
export const LoopConfig = ({ data, onChange, inputColumns = [] }: BaseConfigProps) => {
    const colNames = (Array.isArray(inputColumns) ? inputColumns : [])
        .map((c: any) => (typeof c === 'string' ? c : c?.name || c))
        .filter(Boolean);
    const sortKeys: string[] = Array.isArray(data.sortKeys) ? data.sortKeys
        : (typeof data.sortKeys === 'string' ? data.sortKeys.split(',').map((s: string) => s.trim()).filter(Boolean) : []);
    const toggleSort = (col: string) => {
        const next = sortKeys.includes(col) ? sortKeys.filter(c => c !== col) : [...sortKeys, col];
        onChange('sortKeys', next);
    };
    return (
        <div className="space-y-5">
            {/* Header banner */}
            <div className="p-3 bg-cyan-50 border border-cyan-200 rounded-lg flex items-start gap-2">
                <span className="text-lg"></span>
                <div>
                    <p className="text-xs font-semibold text-cyan-800">Iterate Loop -- Per-row execution</p>
                    <p className="text-[10px] text-cyan-600 mt-0.5">
                        Runs the inner sub-graph <strong>once per row/item</strong>. Best for API calls, LLM prompts, and side-effect tasks.
                    </p>
                </div>
            </div>

            {/* Static Items fallback */}
            <div>
                <Label>Static Items (JSON array)</Label>
                <textarea
                    className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-md bg-white focus:border-cyan-400 focus:outline-none font-mono h-20 resize-none"
                    value={typeof data.staticItems === 'string' ? data.staticItems : JSON.stringify(data.staticItems || [], null, 2)}
                    onChange={(e) => onChange('staticItems', e.target.value)}
                    placeholder='e.g. [{"url": "https://api.example.com/1"}, {"url": "https://api.example.com/2"}]'
                />
                <p className="text-[10px] text-gray-500 mt-1">Leave empty to use upstream node data. Use when there is no input node.</p>
            </div>

            {/* Max Iterations */}
            <div>
                <Label>Max Iterations (safety limit)</Label>
                <Input
                    type="number"
                    value={data.maxIterations ?? 1000}
                    onChange={(e) => onChange('maxIterations', parseInt(e.target.value) || 1000)}
                    placeholder="1000"
                />
            </div>

            {/* Concurrency */}
            <div>
                <Label>Max Concurrency<span className="text-gray-400 font-normal">(parallel threads)</span></Label>
                <div className="flex items-center gap-2">
                    <Input
                        type="number"
                        min={1}
                        max={20}
                        value={data.maxConcurrency ?? 1}
                        onChange={(e) => onChange('maxConcurrency', parseInt(e.target.value) || 1)}
                        className="w-20"
                    />
                    {(data.maxConcurrency ?? 1) > 1 && (
                        <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">Parallel</span>
                    )}
                </div>
                <p className="text-[10px] text-gray-500 mt-1">1 = sequential (default). &gt;1 runs iterations in parallel threads. Use with caution for side-effect tasks.</p>
            </div>

            {/* Error Handling */}
            <div>
                <Label>Error Handling</Label>
                <div className="flex gap-2 mt-1">
                    {[{ v: 'abort', label: ' Abort on error' }, { v: 'continue', label: '> Continue on error' }].map(opt => (
                        <button
                            key={opt.v}
                            type="button"
                            onClick={() => onChange('errorHandling', opt.v)}
                            className={`flex-1 py-1.5 text-xs font-semibold rounded-lg border transition-all ${(data.errorHandling || 'abort') === opt.v
                                ? 'bg-cyan-600 text-white border-cyan-600'
                                : 'bg-white text-gray-600 border-gray-200 hover:border-cyan-300'
                                }`}
                        >{opt.label}</button>
                    ))}
                </div>
            </div>

            {/* Sort Keys */}
            {colNames.length > 0 && (
                <div>
                    <Label>Sort rows before iterating</Label>
                    <div className="border border-gray-200 rounded-lg bg-gray-50 p-2 max-h-28 overflow-y-auto flex flex-wrap gap-2 mt-1">
                        {colNames.map((col: string) => (
                            <label key={col} className="flex items-center gap-1.5 text-xs cursor-pointer">
                                <input type="checkbox" checked={sortKeys.includes(col)} onChange={() => toggleSort(col)} className="rounded" />
                                <span className="font-mono">{col}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {/* Token Reference */}
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mb-2">Template Tokens for Child Node Configs</p>
                <div className="grid grid-cols-2 gap-1 text-[10px] font-mono">
                    {['{{item.COLUMN_NAME}}', '{{index}}', '{{total}}'].map(tok => (
                        <code key={tok} className="bg-cyan-100 text-cyan-800 px-1.5 py-0.5 rounded">{tok}</code>
                    ))}
                </div>
                <p className="text-[10px] text-gray-400 mt-2">Use these in any child node's config fields to inject per-row values.</p>
            </div>
        </div>
    );
};

// 9b.  Transform Loop (ForEach) Configuration -- annotator/set-based
export const ForEachConfig = ({ data, onChange, inputColumns = [] }: BaseConfigProps) => {
    const colNames = (Array.isArray(inputColumns) ? inputColumns : [])
        .map((c: any) => (typeof c === 'string' ? c : c?.name || c))
        .filter(Boolean);
    const sortKeys: string[] = Array.isArray(data.sortKeys) ? data.sortKeys
        : (typeof data.sortKeys === 'string' ? data.sortKeys.split(',').map((s: string) => s.trim()).filter(Boolean) : []);
    const toggleSort = (col: string) => {
        const next = sortKeys.includes(col) ? sortKeys.filter(c => c !== col) : [...sortKeys, col];
        onChange('sortKeys', next);
    };
    return (
        <div className="space-y-4">
            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-start gap-2">
                <span className="text-lg"></span>
                <div>
                    <p className="text-xs font-semibold text-purple-800">Transform Loop -- Set-based (fast)</p>
                    <p className="text-[10px] text-purple-600 mt-0.5">
                        Annotates <strong>all rows at once</strong> using DuckDB. Best for SQL transformations where you need iteration metadata.
                    </p>
                </div>
            </div>

            <div>
                <Label>Maximum Iterations (row cap)</Label>
                <Input
                    type="number"
                    value={data.maxIterations ?? 1000}
                    onChange={(e) => onChange('maxIterations', parseInt(e.target.value) || 1000)}
                    placeholder="1000"
                />
                <p className="text-xs text-gray-500 mt-1">Rows beyond this limit are ignored</p>
            </div>

            <div>
                <Label>Loop Type</Label>
                <Select value={data.loopType || 'row_wise'} onValueChange={(v) => onChange('loopType', v)}>
                    <SelectTrigger className="w-full bg-white border-gray-300">
                        <SelectValue placeholder="Select loop type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-300 shadow-lg">
                        <SelectItem value="row_wise">Row-wise (one row per iteration)</SelectItem>
                        <SelectItem value="batch">Batch (N rows per batch)</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div>
                <Label>Iteration Variable Name</Label>
                <Input
                    value={data.iterationVarName || 'iteration_index'}
                    onChange={(e) => onChange('iterationVarName', e.target.value)}
                    placeholder="iteration_index"
                />
            </div>

            <div className="flex items-center gap-2">
                <input
                    type="checkbox"
                    id="foreach-add-metadata"
                    checked={data.addIterationMetadata !== false}
                    onChange={(e) => onChange('addIterationMetadata', e.target.checked)}
                    className="rounded"
                />
                <Label htmlFor="foreach-add-metadata" className="cursor-pointer">
                    Add iteration metadata columns
                    <span className="block text-[10px] text-gray-400 font-normal mb-0 mt-0.5">is_first, is_last, batch_index, iteration_progress</span>
                </Label>
            </div>

            {colNames.length > 0 && (
                <div>
                    <Label>Sort Keys (order rows before annotating)</Label>
                    <div className="border border-gray-200 rounded-lg bg-gray-50 p-2 max-h-28 overflow-y-auto flex flex-wrap gap-2 mt-1">
                        {colNames.map((col: string) => (
                            <label key={col} className="flex items-center gap-1.5 text-xs cursor-pointer">
                                <input type="checkbox" checked={sortKeys.includes(col)} onChange={() => toggleSort(col)} className="rounded" />
                                <span className="font-mono">{col}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mb-1">Output Columns Added</p>
                <div className="flex flex-wrap gap-1">
                    {['iteration_index', 'total_iterations', 'is_first', 'is_last', 'batch_index', 'iteration_progress'].map(col => (
                        <code key={col} className="bg-purple-100 text-purple-800 text-[10px] px-1.5 py-0.5 rounded font-mono">{col}</code>
                    ))}
                </div>
            </div>
        </div>
    );
};


// Common source fields when upstream columns are not available (avoid manual typing)
const COMMON_CONTENT_FIELDS = ['extracted_text', 'content', 'body', 'text', 'parsed_content', 'ocr_text', 'raw_content'];
const COMMON_FILENAME_FIELDS = ['file_name', 'file_path', 'filename', 'name', 'source_file', 'document_id'];
const COMMON_GROUP_FIELDS = ['ticket_id', 'order_id', 'customer_id', 'claim_id', 'group_key', 'case_id', 'batch_id'];

// 10. Content Appender Configuration (formerly Content Merger)
export const ContentMergerConfig = ({ data, onChange, inputColumns = [] }: BaseConfigProps) => {
    const groupingMode = data.groupingMode || 'custom_pattern';

    const colNames = (Array.isArray(inputColumns) ? inputColumns : [])
        .map((c: any) => (typeof c === 'string' ? c : c?.name || c))
        .filter(Boolean);

    // When no upstream columns: use common fields in dropdown instead of manual Input
    const hasUpstreamColumns = colNames.length > 0;
    const contentOptions = hasUpstreamColumns ? colNames : COMMON_CONTENT_FIELDS;
    const filenameOptions = hasUpstreamColumns ? colNames : COMMON_FILENAME_FIELDS;
    const groupOptions = hasUpstreamColumns ? colNames : COMMON_GROUP_FIELDS;

    const autoDetectedContent = hasUpstreamColumns
        ? (colNames.find(c => /text|content|body|extracted|parsed|ocr/i.test(c)) || 'extracted_text')
        : 'extracted_text';
    const autoDetectedFilename = hasUpstreamColumns
        ? (colNames.find(c => /file|name|filename|path|source/i.test(c)) || 'file_name')
        : 'file_name';

    const contentField = data.contentField || autoDetectedContent;
    const filenameField = data.filenameField || autoDetectedFilename;

    const handleAutoApply = () => {
        if (!data.contentField) onChange('contentField', autoDetectedContent);
        if (!data.filenameField) onChange('filenameField', autoDetectedFilename);
    };

    return (
        <div className="space-y-6">
            <div className="bg-white p-4 rounded-lg border shadow-sm leading-relaxed text-gray-800">
                <div className="mb-3 text-sm font-medium text-gray-900 border-b pb-2">
                    Content Appender Configuration
                </div>

                <div className="space-y-3 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                        <span>I want to find and group documents that have the same</span>
                        <Select value={groupingMode} onValueChange={(value) => onChange('groupingMode', value)}>
                            <SelectTrigger className="w-auto min-w-[140px] h-8 bg-blue-50 border-blue-200 text-blue-700 font-medium">
                                <SelectValue placeholder="Mode" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="field_value">Field Value</SelectItem>
                                <SelectItem value="custom_pattern">Filename Regex</SelectItem>
                            </SelectContent>
                        </Select>

                        {groupingMode === 'field_value' ? (
                            <div className="flex items-center gap-2">
                                <span>in the column</span>
                                <Select
                                    value={groupOptions.includes(data.groupByField || '') ? (data.groupByField || groupOptions[0]) : (groupOptions[0] || 'ticket_id')}
                                    onValueChange={(v) => onChange('groupByField', v)}
                                >
                                    <SelectTrigger className="w-auto min-w-[120px] h-8 bg-indigo-50 border-indigo-200 text-indigo-700 font-medium">
                                        <SelectValue placeholder="Column" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {groupOptions.map((col: string) => (
                                            <SelectItem key={col} value={col}>{col}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <span>.</span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 w-full mt-2">
                                <span>by extracting an ID using this regex:</span>
                                <Input
                                    className="h-8 max-w-[200px] border-amber-300 bg-amber-50 text-amber-900"
                                    value={data.extractIdPattern || 'ticketid[:\\s]*(\\d+)'}
                                    onChange={(e) => onChange('extractIdPattern', e.target.value)}
                                    placeholder="ticketid[:\s]*(\d+)"
                                />
                                <span className="text-xs text-gray-400 italic">(use parentheses to capture)</span>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-wrap items-baseline gap-2 mt-4">
                        <span>For each matched group, I want to append together text from the</span>
                        <Select
                            value={contentOptions.includes(contentField) ? contentField : contentOptions[0]}
                            onValueChange={(v) => onChange('contentField', v)}
                        >
                            <SelectTrigger className="w-auto min-w-[150px] h-8 bg-emerald-50 border-emerald-200 text-emerald-700 font-medium">
                                <SelectValue placeholder="Content Field" />
                            </SelectTrigger>
                            <SelectContent>
                                {contentOptions.map((col: string) => (
                                    <SelectItem key={col} value={col}>{col}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <span>column.</span>
                    </div>

                    <div className="flex flex-wrap items-baseline gap-2">
                        <span>I want to combine them by</span>
                        <Select
                            value={data.mergeStrategy || 'concatenate'}
                            onValueChange={(value) => onChange('mergeStrategy', value)}
                        >
                            <SelectTrigger className="w-auto min-w-[150px] h-8 bg-purple-50 border-purple-200 text-purple-700 font-medium">
                                <SelectValue placeholder="Merge Strategy" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="concatenate">Joining them normally</SelectItem>
                                <SelectItem value="labeled">Adding Filename Headers</SelectItem>
                                <SelectItem value="newline_separated">Adding Separator Lines</SelectItem>
                                <SelectItem value="json_array">Creating a JSON Array</SelectItem>
                            </SelectContent>
                        </Select>

                        <span>using filenames from the</span>
                        <Select
                            value={filenameOptions.includes(filenameField) ? filenameField : filenameOptions[0]}
                            onValueChange={(v) => onChange('filenameField', v)}
                        >
                            <SelectTrigger className="w-auto min-w-[120px] h-8 bg-pink-50 border-pink-200 text-pink-700 font-medium">
                                <SelectValue placeholder="Filename" />
                            </SelectTrigger>
                            <SelectContent>
                                {filenameOptions.map((col: string) => (
                                    <SelectItem key={col} value={col}>{col}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <span>column.</span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mt-3 border-t pt-3 text-xs">
                        <span className="text-gray-600 font-medium">Advanced:</span>
                        <span>Only output the group if it has at least</span>
                        <Input
                            type="number"
                            className="h-7 w-14 text-center border-gray-300 text-xs"
                            value={data.minDocuments || 1}
                            onChange={(e) => onChange('minDocuments', parseInt(e.target.value))}
                        />
                        <span>documents matching.</span>
                    </div>
                </div>
            </div>

            <div className="bg-gray-50 p-3 rounded-md border mt-2">
                <p className="text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wide">Output Fields Generated</p>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                    <div>-<code className="bg-gray-200 px-1 rounded text-[10px]">group_key</code>- Group ID</div>
                    <div>-<code className="bg-gray-200 px-1 rounded text-[10px]">merged_content</code>- Appended text</div>
                    <div>-<code className="bg-gray-200 px-1 rounded text-[10px]">source_files</code>- Attached files</div>
                    <div>-<code className="bg-gray-200 px-1 rounded text-[10px]">document_count</code>- Total items</div>
                </div>
            </div>
        </div>
    );
};

// 11. AI Summarizer Configuration
// System prompt is hardcoded -- this node's purpose is document summarization.
// Users only configure the user message template and AI provider settings.
const AI_SUMMARIZER_SYSTEM_PROMPT = `You are an expert document summarizer. Your task is to read the provided document content and produce a clear, concise, and accurate summary. Focus on key facts, dates, decisions, and action items. Do not add information that is not present in the source text.`;

export const AISummarizerConfig = ({ data, onChange, inputColumns = [] }: BaseConfigProps) => {

    const colNames = (Array.isArray(inputColumns) ? inputColumns : [])
        .map((c: any) => (typeof c === 'string' ? c : c?.name || c))
        .filter(Boolean);

    const autoDetectedTextField = colNames.find(c =>
        /extracted|merged|content|text|body|summary/i.test(c)
    ) || (colNames.length > 0 ? colNames[0] : 'extracted_text');
    const configuredTextFields = parseTextFieldSelection(data.textField);
    const selectedTextFields = configuredTextFields.length > 0
        ? configuredTextFields
        : (autoDetectedTextField ? [autoDetectedTextField] : []);
    const textField = selectedTextFields[0] || autoDetectedTextField;

    useEffect(() => {
        if (!data.textField && colNames.length > 0 && autoDetectedTextField) {
            onChange('textField', autoDetectedTextField);
        }
    }, [colNames.length]);

    const toggleTextField = (column: string) => {
        const next = selectedTextFields.includes(column)
            ? selectedTextFields.filter((f: string) => f !== column)
            : [...selectedTextFields, column];
        onChange('textField', next.join(', '));
    };

    return (
        <div className="space-y-4">
            <div className="bg-blue-50 p-3 rounded-md border border-blue-200 mb-4">
                <p className="text-sm text-blue-800 font-medium">AI Summarization</p>
                <p className="text-xs text-blue-600 mt-1">
                    Summarizes document content using AI. Configure the user message and AI model below.
                </p>
            </div>

            {/* User Message Template -- fully editable */}
            <div>
                <div className="flex items-center justify-between mb-1">
                    <Label>User Message Template</Label>
                    <AIGenerateButton
                        contentType="user message template"
                        placeholder="e.g. Summarize this document and highlight key dates, decisions, and action items"
                        onGenerate={(text) => onChange('userMessageTemplate', text)}
                        context={`Available variables: {{${textField}}}, {{group_key}}, {{document_count}}`}
                    />
                </div>
                <Textarea
                    value={data.userMessageTemplate || `Summarize the following document content:\n\n{{${textField}}}`}
                    onChange={(e) => onChange('userMessageTemplate', e.target.value)}
                    placeholder={`Summarize the following document content:\n\n{{${textField}}}`}
                    rows={5}
                />
                <p className="text-xs text-gray-500 mt-1">
                    Use <code>{'{{field_name}}'}</code>to insert upstream fields. The detected text field is<code>{textField}</code>.
                </p>
            </div>

            {/* Text Field -- auto-detected from upstream */}
            <div>
                <Label>Text Fields (Source)</Label>
                {colNames.length > 0 ? (
                    <div className="border border-gray-300 rounded-md bg-white p-2 max-h-40 overflow-auto space-y-1">
                        {colNames.map((col: string) => (
                            <label key={col} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={selectedTextFields.includes(col)}
                                    onChange={() => toggleTextField(col)}
                                    className="rounded border-gray-300"
                                />
                                <span>{col}</span>
                            </label>
                        ))}
                    </div>
                ) : (
                    <Input
                        value={selectedTextFields.join(', ')}
                        onChange={(e) => onChange('textField', e.target.value)}
                        placeholder="extracted_text, description"
                    />
                )}
                <p className="text-xs text-gray-500 mt-1">
                    {colNames.length > 0
                        ? `Selected ${selectedTextFields.length} column(s). These fields are merged into {{text}}.`
                        : 'Connect an upstream node to see available fields, or type comma-separated field names.'}
                </p>
                <div className="mt-2">
                    <Label className="text-[11px] text-gray-600">Manual Text Fields (comma-separated)</Label>
                    <Input
                        value={selectedTextFields.join(', ')}
                        onChange={(e) => onChange('textField', e.target.value)}
                        placeholder="extracted_text, projectName, description"
                    />
                </div>
            </div>

            <AIProviderConfig data={data} onChange={onChange} />

            <div className="bg-gray-50 p-3 rounded-md border mt-4">
                <p className="text-sm font-medium text-gray-700">Output Fields</p>
                <ul className="text-xs text-gray-600 mt-1 space-y-1">
                    <li>-<code>ai_summary</code>- The generated response</li>
                    <li>-<code>summarized_at</code>- Timestamp</li>
                </ul>
            </div>
        </div>
    );
};

// 11b. LLM Prompt Configuration
export const LLMPromptConfig = ({ data, onChange, inputColumns = [] }: BaseConfigProps & { inputColumns?: { name: string }[] }) => {
    const cols = Array.isArray(inputColumns) ? inputColumns : [];
    const colNames = cols.map((c: any) => (typeof c === 'string' ? c : c?.name || c)).filter(Boolean);
    const autoTextField = colNames.find(c => /extracted|merged|content|text|body/i.test(c)) || (colNames.length > 0 ? colNames[0] : 'extracted_text');
    const configuredTextFields = parseTextFieldSelection(data.textField);
    const selectedTextFields = configuredTextFields.length > 0
        ? configuredTextFields
        : (autoTextField ? [autoTextField] : []);

    const toggleTextField = (column: string) => {
        const next = selectedTextFields.includes(column)
            ? selectedTextFields.filter((f: string) => f !== column)
            : [...selectedTextFields, column];
        onChange('textField', next.join(', '));
    };

    return (
        <div className="space-y-4">
            <div className="bg-violet-50 p-3 rounded-md border border-violet-200 mb-4">
                <p className="text-sm text-violet-800 font-medium">LLM Prompt -- Full Control</p>
                <p className="text-xs text-violet-600 mt-1">
                    System + user template with {'{{field_name}}'} placeholders. JSON mode for structured output.
                </p>
            </div>

            <div>
                <Label>System Prompt</Label>
                <Textarea
                    value={data.systemPrompt || 'You are a helpful assistant.'}
                    onChange={(e) => onChange('systemPrompt', e.target.value)}
                    placeholder="You are a helpful assistant."
                    rows={3}
                />
            </div>

            <div>
                <Label>User Prompt Template</Label>
                <Textarea
                    value={data.userPrompt || '{{text}}'}
                    onChange={(e) => onChange('userPrompt', e.target.value)}
                    placeholder="e.g. Summarize: {{merged_content}}"
                    rows={4}
                />
                <p className="text-xs text-gray-500 mt-1">
                    Use {'{{field_name}}'} to insert upstream columns. {'{{text}}'} maps to the text field.
                </p>
            </div>

            <div>
                <Label>Text Fields (for {'{{text}}'})</Label>
                {colNames.length > 0 ? (
                    <div className="border border-gray-300 rounded-md bg-white p-2 max-h-40 overflow-auto space-y-1">
                        {colNames.map((col: string) => (
                            <label key={col} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={selectedTextFields.includes(col)}
                                    onChange={() => toggleTextField(col)}
                                    className="rounded border-gray-300"
                                />
                                <span>{col}</span>
                            </label>
                        ))}
                    </div>
                ) : (
                    <Input
                        value={selectedTextFields.join(', ')}
                        onChange={(e) => onChange('textField', e.target.value)}
                        placeholder="merged_content, description"
                    />
                )}
                <p className="text-xs text-gray-500 mt-1">
                    {colNames.length > 0
                        ? `Selected ${selectedTextFields.length} column(s). These are merged for {{text}}.`
                        : 'Type comma-separated column names to map into {{text}}.'}
                </p>
                <div className="mt-2">
                    <Label className="text-[11px] text-gray-600">Manual Text Fields (comma-separated)</Label>
                    <Input
                        value={selectedTextFields.join(', ')}
                        onChange={(e) => onChange('textField', e.target.value)}
                        placeholder="projectName, taskName, description"
                    />
                </div>
            </div>

            <div>
                <Label>Output Column Name</Label>
                <Input
                    value={data.outputColumn || 'llm_response'}
                    onChange={(e) => onChange('outputColumn', e.target.value)}
                    placeholder="llm_response"
                />
            </div>

            <div className="flex items-center gap-2 pt-2">
                <input
                    type="checkbox"
                    id="llm-json-mode"
                    checked={data.jsonMode === true}
                    onChange={(e) => onChange('jsonMode', e.target.checked)}
                    className="rounded text-violet-600"
                />
                <Label htmlFor="llm-json-mode" className="text-xs cursor-pointer">JSON output mode</Label>
            </div>

            <AIProviderConfig data={data} onChange={onChange} />
        </div>
    );
};

// 12. Database Lookup Configuration
export const DatabaseLookupConfig = ({ data, onChange, inputColumns = [] }: BaseConfigProps) => {
    const colNames = (Array.isArray(inputColumns) ? inputColumns : [])
        .map((c: any) => (typeof c === 'string' ? c : c?.name || c)).filter(Boolean);
    const lookupMode = data.lookupMode || 'query';
    const [connections, setConnections] = useState<any[]>([]);
    const [tables, setTables] = useState<string[]>([]);
    const [tableColumns, setTableColumns] = useState<string[]>([]);
    const [loadingConnections, setLoadingConnections] = useState(true);
    const [loadingTables, setLoadingTables] = useState(false);
    const [loadingCols, setLoadingCols] = useState(false);

    // Fetch connections on mount
    useEffect(() => {
        let cancelled = false;
        const fetchConnections = async () => {
            setLoadingConnections(true);
            try {
                const token = getToken();
                if (!token) {
                    console.error('[DatabaseLookupConfig] No authentication token found');
                    return;
                }
                const res = await fetch(`${API_BASE_URL}/datasources`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!res.ok) {
                    throw new Error(`Failed to fetch datasources: ${res.status} ${res.statusText}`);
                }
                const data = await res.json();
                if (!cancelled) {
                    setConnections(Array.isArray(data) ? data : []);
                    console.log(`[DatabaseLookupConfig] Loaded ${Array.isArray(data) ? data.length : 0} connections`);
                }
            } catch (e) {
                console.error('[DatabaseLookupConfig] Failed to load connections:', e);
                if (!cancelled) {
                    setConnections([]);
                }
            } finally {
                if (!cancelled) {
                    setLoadingConnections(false);
                }
            }
        };
        fetchConnections();
        return () => { cancelled = true; };
    }, []);

    // Fetch tables when connection changes
    useEffect(() => {
        if (!data.connectionId) {
            setTables([]);
            setTableColumns([]);
            return;
        }
        let cancelled = false;
        const fetchTables = async () => {
            setLoadingTables(true);
            try {
                const token = getToken();
                if (!token) return;
                const res = await fetch(`${API_BASE_URL}/datasources/${data.connectionId}/structure`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!res.ok) {
                    throw new Error(`Failed to fetch tables: ${res.status}`);
                }
                const d = await res.json();
                if (!cancelled) {
                    const tbls = (d.tables || []).map((t: any) => t.name || t);
                    setTables(tbls);
                    console.log(`[DatabaseLookupConfig] Loaded ${tbls.length} tables for connection ${data.connectionId}`);
                }
            } catch (e) {
                console.error('[DatabaseLookupConfig] Failed to load tables:', e);
                if (!cancelled) setTables([]);
            } finally {
                if (!cancelled) setLoadingTables(false);
            }
        };
        fetchTables();
        return () => { cancelled = true; };
    }, [data.connectionId]);

    // Fetch columns when table changes
    useEffect(() => {
        if (!data.connectionId || !data.dbTable) {
            setTableColumns([]);
            return;
        }
        let cancelled = false;
        const fetchColumns = async () => {
            setLoadingCols(true);
            try {
                const token = getToken();
                if (!token) return;
                const res = await fetch(`${API_BASE_URL}/datasources/${data.connectionId}/structure`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!res.ok) {
                    throw new Error(`Failed to fetch columns: ${res.status}`);
                }
                const d = await res.json();
                if (!cancelled) {
                    const tbl = (d.tables || []).find((t: any) => (t.name || t) === data.dbTable);
                    const cols = tbl?.columns?.map((c: any) => c.name || c) || [];
                    setTableColumns(cols);
                    console.log(`[DatabaseLookupConfig] Loaded ${cols.length} columns for table ${data.dbTable}`);
                }
            } catch (e) {
                console.error('[DatabaseLookupConfig] Failed to load columns:', e);
                if (!cancelled) setTableColumns([]);
            } finally {
                if (!cancelled) setLoadingCols(false);
            }
        };
        fetchColumns();
        return () => { cancelled = true; };
    }, [data.connectionId, data.dbTable]);

    const selectedCols: string[] = data.selectColumns === '*' || !data.selectColumns
        ? []
        : data.selectColumns.split(',').map((s: string) => s.trim()).filter(Boolean);

    const toggleColumn = (col: string) => {
        const current = selectedCols;
        const next = current.includes(col)
            ? current.filter(c => c !== col)
            : [...current, col];
        onChange('selectColumns', next.length === 0 ? '*' : next.join(', '));
    };

    const DB_TYPES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 13];
    const TYPE_NAMES: Record<number, string> = {
        0: 'PostgreSQL', 1: 'MySQL', 2: 'SQL Server', 3: 'Oracle',
        4: 'Snowflake', 5: 'BigQuery', 6: 'Redshift', 7: 'Azure SQL', 8: 'Synapse', 13: 'DuckDB'
    };
    const dbConnections = connections.filter(c => DB_TYPES.includes(c.type));

    return (
        <div className="space-y-4">
            {/* Connection -- matching ExecuteSqlConfig pattern EXACTLY */}
            <div>
                <Label>Database Connection</Label>
                <select
                    className="w-full mt-1 text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                    value={data.connectionId || ''}
                    onChange={(e) => {
                        const selected = dbConnections.find((c: any) => c.id === e.target.value);
                        console.log('[DatabaseLookupConfig] v Saving connectionId:', e.target.value, 'name:', selected?.name);
                        onChange('connectionId', e.target.value);
                        onChange('connectionName', selected?.name || '');
                    }}
                    disabled={loadingConnections}
                >
                    <option value="">
                        {loadingConnections
                            ? 'Loading connections...'
                            : dbConnections.length === 0
                                ? 'No database connections available'
                                : 'Select a connection...'}
                    </option>
                    {dbConnections.map((c: any) => (
                        <option key={c.id} value={c.id}>
                            {c.name} ({TYPE_NAMES[c.type] || 'DB'})
                        </option>
                    ))}
                </select>
                {/* Debug info */}
                {data.connectionId && (
                    <p className="text-xs text-green-600 mt-1">
                        v Connected: {dbConnections.find(c => c.id == data.connectionId)?.name || data.connectionId}
                    </p>
                )}
                {!loadingConnections && dbConnections.length === 0 && connections.length > 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                        [!] No database connections found. Please create a database connection first.
                    </p>
                )}
                {!loadingConnections && connections.length === 0 && (
                    <p className="text-xs text-red-600 mt-1">
                        x Failed to load connections. Please check your network connection and try refreshing the page.
                    </p>
                )}
            </div>

            {/* Lookup Mode */}
            <div>
                <Label>Lookup Mode</Label>
                <Select
                    value={lookupMode}
                    onValueChange={(value) => onChange('lookupMode', value)}
                >
                    <SelectTrigger className="w-full bg-white border-gray-300">
                        <SelectValue placeholder="Select lookup mode" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-300 shadow-lg">
                        <SelectItem value="table_lookup">Table Lookup (pick table + columns)</SelectItem>
                        <SelectItem value="query">Custom SQL Query</SelectItem>
                        <SelectItem value="stored_procedure">Stored Procedure</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* TABLE LOOKUP MODE -- dynamic table + column pickers */}
            {lookupMode === 'table_lookup' && (
                <>
                    {/* Table picker */}
                    <div>
                        <Label>Source Table</Label>
                        {loadingTables ? (
                            <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                                <Loader2 className="h-3 w-3 animate-spin" /> Loading tables...
                            </div>
                        ) : (
                            <select
                                className="w-full mt-1 text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                                value={data.dbTable || ""}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    console.log('[DatabaseLookupConfig] v Saving table:', v);
                                    onChange('dbTable', v);
                                    onChange('tableName', v);
                                    onChange('selectColumns', '*');
                                }}
                            >
                                <option value="">{data.connectionId ? 'Select a table...' : 'Select a connection first'}</option>
                                {/* Fallback: always show the saved value even if the tables list failed to load */}
                                {tables.length === 0 && data.dbTable && (
                                    <option value={data.dbTable}>{data.dbTable} [!] (saved -- reload to refresh)</option>
                                )}
                                {tables.map(t => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* Column picker -- checkboxes */}
                    {data.dbTable && (
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <Label>Select Columns</Label>
                                <div className="flex gap-2 text-[10px]">
                                    <button type="button" className="text-purple-600 hover:underline"
                                        onClick={() => onChange('selectColumns', tableColumns.join(', '))}>
                                        All
                                    </button>
                                    <button type="button" className="text-gray-400 hover:text-gray-600"
                                        onClick={() => onChange('selectColumns', '*')}>
                                        Reset
                                    </button>
                                </div>
                            </div>
                            {loadingCols ? (
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <Loader2 className="h-3 w-3 animate-spin" /> Loading columns...
                                </div>
                            ) : tableColumns.length > 0 ? (
                                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50 p-2 space-y-1">
                                    {tableColumns.map(col => (
                                        <label key={col} className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-1 py-0.5">
                                            <input
                                                type="checkbox"
                                                className="rounded border-gray-300 text-purple-600 h-3.5 w-3.5"
                                                checked={selectedCols.includes(col) || data.selectColumns === '*'}
                                                onChange={() => toggleColumn(col)}
                                            />
                                            <span className="text-xs text-gray-700 font-mono">{col}</span>
                                        </label>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-xs text-gray-400 italic">No columns found for this table.</p>
                            )}
                            <p className="text-[10px] text-gray-400 mt-1">
                                Selected: <code>{data.selectColumns || '*'}</code>
                            </p>
                        </div>
                    )}

                    {/* Lookup key */}
                    <div>
                        <Label>Key Column (match on)</Label>
                        {tableColumns.length > 0 ? (
                            <Select
                                value={data.dbKeyColumn || ''}
                                onValueChange={(v) => onChange('dbKeyColumn', v)}
                            >
                                <SelectTrigger className="w-full bg-white border-gray-300">
                                    <SelectValue placeholder="Select key column..." />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-gray-300 shadow-lg">
                                    {tableColumns.map(col => (
                                        <SelectItem key={col} value={col}>{col}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        ) : (
                            <Input
                                value={data.dbKeyColumn || 'id'}
                                onChange={(e) => onChange('dbKeyColumn', e.target.value)}
                                placeholder="id"
                            />
                        )}
                        <p className="text-xs text-gray-500 mt-1">Column to match against the lookup field from input</p>
                    </div>

                    <div>
                        <Label>Lookup Field (from input)</Label>
                        {colNames.length > 0 ? (
                            <Select value={data.lookupField || ''} onValueChange={(v) => onChange('lookupField', v)}>
                                <SelectTrigger className="w-full bg-white border-gray-300">
                                    <SelectValue placeholder="Select lookup field" />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-gray-300 shadow-lg">
                                    {colNames.map((col: string) => (
                                        <SelectItem key={col} value={col}>{col}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        ) : (
                            <Input
                                value={data.lookupField || 'group_key'}
                                onChange={(e) => onChange('lookupField', e.target.value)}
                                placeholder="group_key"
                            />
                        )}
                        <p className="text-xs text-gray-500 mt-1">Field from the workflow input to use as the match value</p>
                    </div>
                </>
            )}

            {/* CUSTOM SQL MODE */}
            {lookupMode === 'query' && (
                <>
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <Label>SQL Query</Label>
                            <AIGenerateButton
                                contentType="SQL query"
                                placeholder="e.g. Write a SQL query to look up a user's account details by email address"
                                onGenerate={(text) => onChange('sqlQuery', text)}
                                context="Use :param_name for parameters mapped from input fields. Example: :group_key, :email"
                            />
                        </div>
                        <Textarea
                            value={data.sqlQuery || data.query || ''}
                            onChange={(e) => onChange('sqlQuery', e.target.value)}
                            placeholder="SELECT * FROM table WHERE id = :id"
                            rows={5}
                            className="font-mono text-sm"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Use <code>:param_name</code>for parameters. Example:<code>SELECT * FROM users WHERE email = :email</code>
                        </p>
                    </div>

                    {/* Parameters Mapping */}
                    <div>
                        <Label>Query Parameters</Label>
                        <div className="space-y-2 mt-2">
                            {(Array.isArray(data.parameters) ? data.parameters : []).map((param: any, idx: number) => (
                                <div key={idx} className="flex gap-2 items-center bg-gray-50 p-2 rounded border">
                                    <Input
                                        placeholder="param_name"
                                        value={param.paramName || ''}
                                        onChange={(e) => {
                                            const current = Array.isArray(data.parameters) ? data.parameters : [];
                                            const updated = [...current];
                                            updated[idx] = { ...param, paramName: e.target.value };
                                            console.log('[DatabaseLookup] Updated parameters:', updated);
                                            onChange('parameters', updated);
                                        }}
                                        className="flex-1 text-sm"
                                    />
                                    <span className="text-gray-400">(from)</span>
                                    <select
                                        className="flex-1 text-sm px-2 py-1.5 border rounded bg-white"
                                        value={param.sourceField || ''}
                                        onChange={(e) => {
                                            const current = Array.isArray(data.parameters) ? data.parameters : [];
                                            const updated = [...current];
                                            updated[idx] = { ...param, sourceField: e.target.value };
                                            console.log('[DatabaseLookup] Updated parameters:', updated);
                                            onChange('parameters', updated);
                                        }}
                                    >
                                        <option value="">Select input field...</option>
                                        {colNames.map(col => (
                                            <option key={col} value={col}>{col}</option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            const current = Array.isArray(data.parameters) ? data.parameters : [];
                                            const updated = current.filter((_: any, i: number) => i !== idx);
                                            console.log('[DatabaseLookup] Removed parameter, remaining:', updated);
                                            onChange('parameters', updated);
                                        }}
                                        className="text-red-500 hover:bg-red-50 p-1 rounded"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    const current = Array.isArray(data.parameters) ? data.parameters : [];
                                    const updated = [...current, { paramName: '', sourceField: '' }];
                                    console.log('[DatabaseLookup] Added parameter, total:', updated.length);
                                    onChange('parameters', updated);
                                }}
                                className="w-full text-sm text-purple-600 hover:bg-purple-50 border border-dashed border-purple-300 rounded py-2 flex items-center justify-center gap-1"
                            >
                                <Plus className="h-4 w-4" /> Add Parameter
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            Map SQL parameters (:param_name) to input columns
                        </p>
                    </div>
                </>
            )}

            {/* STORED PROCEDURE MODE */}
            {lookupMode === 'stored_procedure' && (
                <div>
                    <Label>Stored Procedure</Label>
                    <Input
                        value={data.storedProcedure || ''}
                        onChange={(e) => onChange('storedProcedure', e.target.value)}
                        placeholder="Schema.ProcedureName"
                    />
                    <p className="text-xs text-gray-500 mt-1">Full stored procedure name</p>
                </div>
            )}

            <div className="bg-gray-50 p-3 rounded-md border mt-4">
                <p className="text-sm font-medium text-gray-700">Output</p>
                <p className="text-xs text-gray-600 mt-1">
                    Returns the result as fields in the workflow (first row, or list of rows if multiple).
                </p>
            </div>
        </div>
    );
};

// 13. Template Renderer Configuration
export const TemplateRendererConfig = ({ data, onChange }: BaseConfigProps) => {
    const templateMode = data.templateMode || 'docx';
    const templateSource = data.templateSource || 'file';
    const { token } = useAuth ? useAuth() : { token: getToken() };
    const [connections, setConnections] = useState<Connection[]>([]);
    const [loadingConnections, setLoadingConnections] = useState(false);

    // Ensure outputMode is always set for backend file saving
    useEffect(() => {
        const updates: Record<string, any> = {};

        if (!data.outputMode) {
            updates.outputMode = 'save';
        }

        if (!data.outputDirectory) {
            updates.outputDirectory = '/tmp/generated_docs';
        }

        // Apply all updates at once if any are needed
        if (Object.keys(updates).length > 0) {
            Object.entries(updates).forEach(([key, value]) => {
                onChange(key, value);
            });
        }
    }, []);

    // Fetch connections for template source
    useEffect(() => {
        if ((templateSource === 'sharepoint' || templateSource === 's3' || templateSource === 'connection') && token) {
            setLoadingConnections(true);
            getConnections(token)
                .then((data) => setConnections(Array.isArray(data) ? data : []))
                .catch(() => setConnections([]))
                .finally(() => setLoadingConnections(false));
        }
    }, [templateSource, token]);

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const toastId = toast.loading(`Uploading ${file.name}...`);
        try {
            const formData = new FormData();
            formData.append('file', file);

            const res = await fetch(`${API_BASE_URL}/flows/ops/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token || getToken()}`
                },
                body: formData
            }).then(r => r.json());

            if (res && res.filePath) {
                onChange('templatePath', res.filePath);
                toast.success(`v File uploaded successfully: ${file.name}`, {
                    id: toastId,
                    duration: 5000,
                    description: `Path: ${res.filePath}`
                });
            } else {
                toast.error("Upload failed: No path returned", { id: toastId });
            }
        } catch (err) {
            console.error(err);
            toast.error(`Upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`, { id: toastId });
        }
    };

    return (
        <div className="space-y-4">
            {/* Important Notice */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                <p className="text-sm font-semibold text-blue-900 mb-1">Output Format Notice</p>
                <p className="text-xs text-blue-800">
                    This node generates <strong>DOCX</strong>(Word) or<strong>TXT</strong> (plain text) files only.
                    <br />
                    <strong>For PDF output:</strong> Use Microsoft Word to convert DOCX -&gt; PDF, or add a document conversion node after this one.
                </p>
            </div>

            <div>
                <Label>Template Mode</Label>
                <Select
                    value={templateMode}
                    onValueChange={(value) => onChange('templateMode', value)}
                >
                    <SelectTrigger className="w-full bg-white border-gray-300">
                        <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-300 shadow-lg">
                        <SelectItem value="docx">DOCX Template</SelectItem>
                        <SelectItem value="text">Plain Text Template</SelectItem>
                    </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                    {templateMode === 'docx'
                        ? 'Generic DOCX template with {{field_name}} placeholders'
                        : 'Plain text template with {{field_name}} syntax'}
                </p>
            </div>

            {/* Template Source Configuration */}
            {templateMode === 'docx' && (
                <>
                    <div>
                        <Label>Template Source</Label>
                        <Select
                            value={templateSource}
                            onValueChange={(value) => onChange('templateSource', value)}
                        >
                            <SelectTrigger className="w-full bg-white border-gray-300">
                                <SelectValue placeholder="Select source" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-300 shadow-lg">
                                <SelectItem value="file">Local Filesystem</SelectItem>
                                <SelectItem value="url">Direct URL</SelectItem>
                                <SelectItem value="sharepoint">Microsoft SharePoint</SelectItem>
                                <SelectItem value="s3">AWS S3</SelectItem>
                                <SelectItem value="azure">Azure Blob Storage</SelectItem>
                                <SelectItem value="gdrive">Google Drive</SelectItem>
                                <SelectItem value="connection">Use Connection</SelectItem>
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500 mt-1">
                            Where your template file is stored
                        </p>
                    </div>

                    {/* Local Filesystem */}
                    {templateSource === 'file' && (
                        <>
                            <div>
                                <Label>Upload Template File</Label>
                                <div className={`border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center transition-colors ${data.templatePath
                                    ? 'border-green-300 bg-green-50'
                                    : 'border-gray-300 bg-gray-50 hover:bg-white'
                                    }`}>
                                    <input
                                        type="file"
                                        className="hidden"
                                        id="template-upload"
                                        accept=".docx,.doc"
                                        onChange={handleFileUpload}
                                    />
                                    <label
                                        htmlFor="template-upload"
                                        className="cursor-pointer flex flex-col items-center gap-2"
                                    >
                                        {data.templatePath ? (
                                            <>
                                                <CheckCircle2 className="w-8 h-8 text-green-600" />
                                                <span className="text-sm text-green-700 font-medium">File uploaded successfully</span>
                                                <span className="text-xs text-green-600 max-w-full truncate px-2">
                                                    {data.templatePath.split(/[/\\]/).pop()}
                                                </span>
                                                <span className="text-xs text-gray-500">Click to upload a different file</span>
                                            </>
                                        ) : (
                                            <>
                                                <FolderOpen className="w-8 h-8 text-gray-400" />
                                                <span className="text-sm text-gray-600">Click to upload template file</span>
                                                <span className="text-xs text-gray-400">.docx files only</span>
                                            </>
                                        )}
                                    </label>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Upload a DOCX template file with {'{{field_name}}'} placeholders
                                </p>
                            </div>

                            {data.templatePath && (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                                    <p className="text-xs text-green-800">
                                        <strong>Uploaded:</strong> {data.templatePath}
                                    </p>
                                </div>
                            )}

                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <span className="w-full border-t border-gray-200" />
                                </div>
                                <div className="relative flex justify-center text-xs uppercase">
                                    <span className="bg-white px-2 text-gray-500">Or specify path</span>
                                </div>
                            </div>

                            <div>
                                <Label>Template File Path</Label>
                                <Input
                                    value={data.templatePath || ''}
                                    onChange={(e) => onChange('templatePath', e.target.value)}
                                    placeholder="template.docx or D:\\templates\\template.docx"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Windows (D:\templates\) or Linux (/tmp/templates/) paths, or filename to search artifacts folder
                                </p>
                            </div>
                        </>
                    )}

                    {/* Direct URL */}
                    {templateSource === 'url' && (
                        <div>
                            <Label>Template URL<span className="text-red-500">*</span></Label>
                            <Input
                                value={data.templatePath || ''}
                                onChange={(e) => onChange('templatePath', e.target.value)}
                                placeholder="https://example.com/templates/template.docx"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Direct URL to download the template file (must be publicly accessible or include auth in URL)
                            </p>
                        </div>
                    )}

                    {/* SharePoint */}
                    {templateSource === 'sharepoint' && (
                        <div className="space-y-4">
                            <ServiceConnectionSelect
                                service="sharepoint"
                                value={data.connectionId}
                                onChange={(v) => onChange('connectionId', v)}
                                label="SharePoint Credentials"
                            />

                            {(!data.connectionId || data.connectionId === 'new') && (
                                <>
                                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                                        <p className="text-xs text-purple-900 font-medium mb-1">Microsoft Graph API Connection</p>
                                        <p className="text-xs text-purple-800">
                                            Uses Azure App Registration with Microsoft Graph API.
                                            <br /><strong>Note:</strong> Leave credentials blank to use connection ID or tenant default integration.
                                        </p>
                                    </div>

                                    <div>
                                        <Label>Tenant ID<span className="text-gray-400 font-normal ml-1">(Optional)</span></Label>
                                        <Input
                                            value={data.tenantId || ''}
                                            onChange={(e) => onChange('tenantId', e.target.value)}
                                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Azure Portal -&gt; Microsoft Entra ID -&gt; Overview -&gt; Tenant ID
                                        </p>
                                    </div>

                                    <div>
                                        <Label>App Client ID<span className="text-gray-400 font-normal ml-1">(Optional)</span></Label>
                                        <Input
                                            value={data.clientId || ''}
                                            onChange={(e) => onChange('clientId', e.target.value)}
                                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Azure Portal -&gt; App Registrations -&gt; Your App -&gt; Application (client) ID
                                        </p>
                                    </div>

                                    <div>
                                        <Label>App Client Secret<span className="text-gray-400 font-normal ml-1">(Optional)</span></Label>
                                        <Input
                                            type="password"
                                            value={data.clientSecret || ''}
                                            onChange={(e) => onChange('clientSecret', e.target.value)}
                                            placeholder="Client secret value"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Azure Portal -&gt; App Registrations -&gt; Certificates & secrets -&gt; New client secret
                                        </p>
                                    </div>
                                </>
                            )}

                            <div className="border-t pt-3 mt-4">
                                <Label className="text-sm font-semibold text-gray-700">SharePoint Location</Label>
                            </div>

                            <div>
                                <Label>SharePoint Domain<span className="text-red-500">*</span></Label>
                                <div className="flex items-center gap-1">
                                    <Input
                                        value={data.sharepointDomain || ''}
                                        onChange={(e) => onChange('sharepointDomain', e.target.value)}
                                        placeholder="contoso"
                                        className="flex-1"
                                    />
                                    <span className="text-sm text-gray-500">.sharepoint.com</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Your organization's SharePoint domain (before .sharepoint.com)
                                </p>
                            </div>

                            <div>
                                <Label>Site Name<span className="text-red-500">*</span></Label>
                                <Input
                                    value={data.siteName || ''}
                                    onChange={(e) => onChange('siteName', e.target.value)}
                                    placeholder="TeamSite"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Just the site name from: contoso.sharepoint.com/sites/<strong>TeamSite</strong>
                                </p>
                            </div>

                            <div>
                                <Label>Template File Path<span className="text-red-500">*</span></Label>
                                <Input
                                    value={data.templatePath || ''}
                                    onChange={(e) => onChange('templatePath', e.target.value)}
                                    placeholder="/Shared Documents/Templates/template.docx"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Full path to template file within SharePoint site (include library name)
                                </p>
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                                <p className="text-xs text-blue-900 font-medium mb-1">App Permissions Required:</p>
                                <p className="text-xs text-blue-800">
                                    Sites.Read.All, Files.Read.All (Application permissions in Azure)
                                </p>
                            </div>
                        </div>
                    )}

                    {/* AWS S3 */}
                    {templateSource === 's3' && (
                        <div className="space-y-4">
                            <ServiceConnectionSelect
                                service="aws_s3"
                                value={data.connectionId}
                                onChange={(v) => onChange('connectionId', v)}
                                label="AWS S3 Credentials"
                            />

                            <div>
                                <Label>Template File Path<span className="text-red-500">*</span></Label>
                                <Input
                                    value={data.templatePath || ''}
                                    onChange={(e) => onChange('templatePath', e.target.value)}
                                    placeholder="s3://bucket-name/templates/template.docx"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    S3 path format: s3://bucket-name/path/to/template.docx
                                </p>
                            </div>

                            {(!data.connectionId || data.connectionId === 'new') && (
                                <>
                                    <div>
                                        <Label>AWS Access Key ID</Label>
                                        <Input
                                            type="password"
                                            value={data.awsAccessKey || ''}
                                            onChange={(e) => onChange('awsAccessKey', e.target.value)}
                                            placeholder="Or use AWS_ACCESS_KEY_ID env"
                                        />
                                    </div>

                                    <div>
                                        <Label>AWS Secret Access Key</Label>
                                        <Input
                                            type="password"
                                            value={data.awsSecretKey || ''}
                                            onChange={(e) => onChange('awsSecretKey', e.target.value)}
                                            placeholder="Or use AWS_SECRET_ACCESS_KEY env"
                                        />
                                    </div>

                                    <div>
                                        <Label>AWS Region</Label>
                                        <Input
                                            value={data.awsRegion || 'us-east-1'}
                                            onChange={(e) => onChange('awsRegion', e.target.value)}
                                            placeholder="us-east-1"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            AWS region where your S3 bucket is located. Env fallback: AWS_REGION
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Azure Blob Storage */}
                    {templateSource === 'azure' && (
                        <div className="space-y-4">
                            <ServiceConnectionSelect
                                service="azure_blob"
                                value={data.connectionId}
                                onChange={(v) => onChange('connectionId', v)}
                                label="Azure Blob Credentials"
                            />

                            <div>
                                <Label>Template File Path<span className="text-red-500">*</span></Label>
                                <Input
                                    value={data.templatePath || ''}
                                    onChange={(e) => onChange('templatePath', e.target.value)}
                                    placeholder="azure://container-name/templates/template.docx"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Azure path format: azure://container-name/path/to/template.docx
                                </p>
                            </div>

                            {(!data.connectionId || data.connectionId === 'new') && (
                                <div>
                                    <Label>Azure Storage Connection String</Label>
                                    <Input
                                        type="password"
                                        value={data.azureConnectionString || ''}
                                        onChange={(e) => onChange('azureConnectionString', e.target.value)}
                                        placeholder="DefaultEndpointsProtocol=https;AccountName=..."
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Get from Azure Portal -&gt; Storage Account -&gt; Access Keys
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Google Drive */}
                    {templateSource === 'gdrive' && (
                        <>
                            <div>
                                <Label>File ID<span className="text-red-500">*</span></Label>
                                <Input
                                    value={data.templatePath || ''}
                                    onChange={(e) => onChange('templatePath', e.target.value)}
                                    placeholder="File ID from drive.google.com/file/d/XXXXX"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Right-click file -&gt; Share -&gt; Copy link. ID is the string after /file/d/
                                </p>
                            </div>

                            <div>
                                <Label>Service Account JSON<span className="text-red-500">*</span></Label>
                                <Textarea
                                    value={data.gdriveServiceAccountKey || ''}
                                    onChange={(e) => onChange('gdriveServiceAccountKey', e.target.value)}
                                    placeholder='Paste full JSON from Google Cloud Console'
                                    rows={4}
                                    className="font-mono text-xs"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Google Cloud Console -&gt; IAM -&gt; Service Accounts -&gt; Create key (JSON). Needs Drive API enabled.
                                </p>
                            </div>
                        </>
                    )}

                    {/* Connection-based */}
                    {templateSource === 'connection' && (
                        <>
                            <div>
                                <Label>Template File Path<span className="text-red-500">*</span></Label>
                                <Input
                                    value={data.templatePath || ''}
                                    onChange={(e) => onChange('templatePath', e.target.value)}
                                    placeholder="templates/template.docx"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Path relative to the connection root
                                </p>
                            </div>

                            <div>
                                <Label>Connection ID</Label>
                                {loadingConnections ? (
                                    <div className="text-xs text-gray-500 p-2 bg-gray-50 rounded border">Loading connections...</div>
                                ) : connections.length > 0 ? (
                                    <Select
                                        value={data.templateConnectionId || ''}
                                        onValueChange={(value) => onChange('templateConnectionId', value)}
                                    >
                                        <SelectTrigger className="w-full bg-white border-gray-300">
                                            <SelectValue placeholder="Select connection" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white border-gray-300 shadow-lg max-h-60 overflow-y-auto">
                                            {connections.map((conn) => (
                                                <SelectItem key={conn.id} value={conn.id}>
                                                    {conn.name ? `${conn.name} (${conn.id})` : conn.id}
                                                </SelectItem>
                                            ))}
                                            <SelectItem value="custom">Other (enter manually)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                ) : (
                                    <Input
                                        value={data.templateConnectionId || ''}
                                        onChange={(e) => onChange('templateConnectionId', e.target.value)}
                                        placeholder="gc_... or tc_..."
                                    />
                                )}
                                {(!connections.length || data.templateConnectionId === 'custom') && (
                                    <div className="mt-2">
                                        <Input
                                            value={data.templateConnectionId || ''}
                                            onChange={(e) => onChange('templateConnectionId', e.target.value)}
                                            placeholder="gc_... or tc_..."
                                        />
                                    </div>
                                )}
                                <p className="text-xs text-gray-500 mt-1">
                                    Global (gc_) or Tenant (tc_) connection ID for authentication
                                </p>
                            </div>
                        </>
                    )}
                </>
            )}

            {templateMode === 'text' && (
                <div>
                    <div className="flex items-center justify-between mb-1">
                        <Label>Template Text</Label>
                        <AIGenerateButton
                            contentType="text template"
                            placeholder="e.g. Create a plain text report template for a claim summary with account, charges, and AI summary fields"
                            onGenerate={(text) => onChange('templateText', text)}
                            context="Available variables: {{ai_summary}}, {{group_key}}, {{rendered_at}} and any database lookup fields"
                        />
                    </div>
                    <Textarea
                        value={data.templateText || ''}
                        onChange={(e) => onChange('templateText', e.target.value)}
                        placeholder={`Account: {{Account}}\nSummary: {{ai_summary}}\n\nGenerated on {{rendered_at}}`}
                        rows={10}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Use {'{{field_name}}'} for variables. Supports dynamic field substitution.
                    </p>
                </div>
            )}

            {/* Output Directory - Required for Backend File Saving */}
            <div>
                <Label>Output Directory<span className="text-red-500">*</span></Label>
                <Input
                    value={data.outputDirectory || '/tmp/generated_docs'}
                    onChange={(e) => onChange('outputDirectory', e.target.value)}
                    placeholder="D:\output or /tmp/generated_docs"
                />
                <p className="text-xs text-gray-500 mt-1">
                    Local directory where rendered documents will be saved. Backend creates this directory automatically.
                </p>
            </div>

            <div className="bg-gray-50 p-3 rounded-md border mt-4">
                <p className="text-sm font-medium text-gray-700">Output Fields</p>
                <ul className="text-xs text-gray-600 mt-1 space-y-1">
                    <li>-<code>rendered_output</code>- Status message with filename</li>
                    <li>-<code>output_file_path</code>- Full path to generated file</li>
                    <li>-<code>rendered_at</code>- Timestamp</li>
                </ul>
                <p className="text-xs text-gray-600 mt-2">
                    <strong>Note:</strong> Template Renderer generates DOCX or TXT files only. For PDF conversion, use a document conversion node or FILE_OUTPUT node downstream.
                </p>
            </div>
        </div>
    );
};

// 14. Approval Configuration
export const ApprovalConfig = ({ data, onChange, inputColumns = [] }: BaseConfigProps) => {
    const { user } = useAuth();
    const mode = 'manual';
    const [allUsers, setAllUsers] = useState<any[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [userSearch, setUserSearch] = useState('');

    const colNames = (Array.isArray(inputColumns) ? inputColumns : [])
        .map((c: any) => (typeof c === 'string' ? c : c?.name || c))
        .filter(Boolean);

    // Load users scoped to the current user's tenant (Platform Admins pass tenantId
    // so the backend restricts the list to that tenant, preventing cross-tenant leakage)
    const tenantId = user?.tenantId;
    useEffect(() => {
        setLoadingUsers(true);
        fetchAllUsers(tenantId || undefined)
            .then(users => setAllUsers(users))
            .catch(() => setAllUsers([]))
            .finally(() => setLoadingUsers(false));
    }, [tenantId]);

    // Current selection: extract email from "user:<email>" format
    const currentEmail = (data.assignee || '').startsWith('user:')
        ? (data.assignee || '').replace('user:', '')
        : (data.assignee || '');

    // Filtered user list based on search (name, email, or role)
    const filteredUsers = userSearch.trim()
        ? allUsers.filter(u => {
            const name = (u.name || '').toLowerCase();
            const email = (u.email || '').toLowerCase();
            const role = (u.role || '').toLowerCase();
            const q = userSearch.toLowerCase();
            return name.includes(q) || email.includes(q) || role.includes(q);
        })
        : allUsers;

    return (
        <div className="space-y-4">
            {/* Manual Mode - Human Review Only */}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start gap-3">
                    <div className="w-4 h-4 rounded-full border-2 mt-0.5 flex items-center justify-center border-amber-500 bg-amber-500">
                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                    </div>
                    <div className="flex-grow">
                        <p className="text-sm font-medium text-gray-900">Manual (Human Review)</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Pause flow and wait for human approval
                        </p>
                    </div>
                </div>
            </div>

            {/* Manual Approval Settings */}
            <div className="space-y-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div>
                    <Label>Task Title</Label>
                    <Input
                        value={data.taskTitle || ''}
                        onChange={(e) => onChange('taskTitle', e.target.value)}
                        placeholder="Review AI Summary"
                    />
                    <p className="text-xs text-amber-700 mt-1">
                        Title shown in the approval task list
                    </p>
                </div>

                <div>
                    <Label>Assign To</Label>
                    <div className="space-y-2 w-full mt-2">
                        {/* Search filter */}
                        <input
                            type="text"
                            value={userSearch}
                            onChange={(e) => setUserSearch(e.target.value)}
                            placeholder="Search by name or email..."
                            className="w-full text-xs h-8 bg-white border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-amber-400"
                        />
                        {/* Search hint */}
                        <p className="text-[10px] text-gray-400">Search by name, email or role</p>
                        {/* User list */}
                        {loadingUsers ? (
                            <div className="text-xs text-amber-600 px-1 py-2">Loading users...</div>
                        ) : (
                            <select
                                size={5}
                                value={currentEmail}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    onChange('assignee', val ? `user:${val}` : '');
                                }}
                                className="w-full text-xs bg-white border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-amber-400 cursor-pointer"
                            >
                                <option value="">-- No assignee --</option>
                                {filteredUsers.map((u) => {
                                    const name = u.name || u.email;
                                    const role = u.role ? ` . ${u.role}` : '';
                                    const label = `${name}${role} (${u.email})`;
                                    return (
                                        <option key={u.id || u.email} value={u.email}>
                                            {label}
                                        </option>
                                    );
                                })}
                                {filteredUsers.length === 0 && userSearch && (
                                    <option disabled value="">No matches found</option>
                                )}
                            </select>
                        )}
                        {currentEmail && (
                            <p className="text-[10px] text-amber-600">
                                Approval notification will be sent to <strong>{currentEmail}</strong>
                            </p>
                        )}
                    </div>
                </div>


                <div>
                    <div className="flex items-center justify-between mb-1">
                        <Label>Description / Instructions</Label>
                        <AIGenerateButton
                            contentType="approval instructions"
                            placeholder="e.g. Write instructions for a reviewer to check the AI summary for accuracy"
                            onGenerate={(text) => onChange('description', text)}
                        />
                    </div>
                    <Textarea
                        value={data.description || ''}
                        onChange={(e) => onChange('description', e.target.value)}
                        placeholder="Please review the AI-generated summary for accuracy before proceeding..."
                        rows={3}
                    />
                </div>

                <div>
                    <Label>Timeout (Hours)</Label>
                    <Input
                        type="number"
                        value={data.timeoutHours || ''}
                        onChange={(e) => onChange('timeoutHours', e.target.value ? parseInt(e.target.value) : null)}
                        placeholder="24 (leave empty for no timeout)"
                    />
                    <p className="text-xs text-amber-700 mt-1">
                        Auto-reject after this many hours (empty = no timeout)
                    </p>
                </div>

                <div>
                    <Label>Preview Columns</Label>
                    {colNames.length > 0 ? (
                        <div className="space-y-2">
                            <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto p-2 bg-white border rounded-md">
                                {colNames.map((col: string) => {
                                    const selected = (data.previewColumns || []).includes(col);
                                    return (
                                        <label key={col} className="flex items-center gap-1.5 text-xs cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selected}
                                                onChange={() => {
                                                    const prev = data.previewColumns || [];
                                                    const next = selected ? prev.filter((c: string) => c !== col) : [...prev, col];
                                                    onChange('previewColumns', next);
                                                }}
                                                className="rounded border-gray-300"
                                            />
                                            <span>{col}</span>
                                        </label>
                                    );
                                })}
                            </div>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    className="text-[10px] text-amber-700 hover:underline"
                                    onClick={() => onChange('previewColumns', colNames)}
                                >
                                    Select All
                                </button>
                                <button
                                    type="button"
                                    className="text-[10px] text-amber-700 hover:underline"
                                    onClick={() => onChange('previewColumns', [])}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                    ) : (
                        <Input
                            value={data.previewColumns?.join(', ') || ''}
                            onChange={(e) => onChange('previewColumns', e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean))}
                            placeholder="ticket_id, summary, claim_amount"
                        />
                    )}
                    <p className="text-xs text-amber-700 mt-1">
                        {colNames.length > 0 ? 'Columns to show reviewer (empty = all)' : 'Connect upstream for column picker, or type comma-separated'}
                    </p>
                </div>
            </div>

            {/* Info Box */}
            <div className="p-3 rounded-lg border bg-amber-50 border-amber-200">
                <p className="text-xs font-medium mb-1 text-amber-900">
                    Human-in-the-Loop
                </p>
                <p className="text-xs text-amber-800">
                    Connect Email/Teams nodes to YES/NO outputs for notifications.
                </p>
            </div>
        </div>
    );
};

// 17. File Output Configuration
export const FileOutputConfig = ({ data, onChange }: BaseConfigProps) => {
    const storageType = data.storageType || 'local';
    const exportFormat = data.exportFormat || 'CSV';
    const { token } = useAuth ? useAuth() : { token: getToken() };
    const [connections, setConnections] = useState<Connection[]>([]);
    const [loadingConnections, setLoadingConnections] = useState(false);

    useEffect(() => {
        if ((storageType === 's3' || storageType === 'sharepoint' || storageType === 'connection') && token) {
            setLoadingConnections(true);
            getConnections(token)
                .then((data) => setConnections(Array.isArray(data) ? data : []))
                .catch(() => setConnections([]))
                .finally(() => setLoadingConnections(false));
        }
    }, [storageType, token]);

    const [localDownloadPath, setLocalDownloadPath] = useState(data.downloadPath || '');
    const [folderSelected, setFolderSelected] = useState(!!data._handleKey);

    return (
        <div className="space-y-4">

            {/* 1. Storage Type */}
            <div>
                <Label>Storage Type</Label>
                <Select
                    value={storageType}
                    onValueChange={(value) => onChange('storageType', value)}
                >
                    <SelectTrigger className="w-full bg-white border-gray-300">
                        <SelectValue placeholder="Select storage type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-300 shadow-lg">
                        <SelectItem value="local">Download to Local System</SelectItem>
                        <SelectItem value="s3">AWS S3</SelectItem>
                        <SelectItem value="azure">Azure Blob Storage</SelectItem>
                        <SelectItem value="sharepoint">Microsoft SharePoint</SelectItem>
                        <SelectItem value="gdrive">Google Drive</SelectItem>
                        <SelectItem value="onedrive">Microsoft OneDrive</SelectItem>
                    </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Where to save the output files</p>
            </div>

            {/* 2. Export Format */}
            <div>
                <Label>Export Format</Label>
                <Select
                    value={exportFormat}
                    onValueChange={(value) => onChange('exportFormat', value)}
                >
                    <SelectTrigger className="w-full bg-white border-gray-300">
                        <SelectValue placeholder="Select format" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-300 shadow-lg">
                        <SelectItem value="CSV">CSV</SelectItem>
                        <SelectItem value="EXCEL">Excel (XLSX)</SelectItem>
                        <SelectItem value="PDF">PDF</SelectItem>
                        <SelectItem value="JSON">JSON</SelectItem>
                        <SelectItem value="PARQUET">Parquet</SelectItem>
                    </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Format used when exporting table data from the previous node</p>
            </div>

            {/* 3. Destination / Download */}
            <div>
                {storageType === 'local' ? (
                    <div className="space-y-2">
                        <div>
                            <Label>Download Location</Label>
                            <div className="flex gap-2 mt-1">
                                <Input
                                    value={localDownloadPath}
                                    onChange={(e) => {
                                        setLocalDownloadPath(e.target.value);
                                        onChange('downloadPath', e.target.value);
                                    }}
                                    placeholder="e.g. C:\Users\John\Downloads\Reports"
                                    className="flex-1 text-xs"
                                />
                                <button
                                    type="button"
                                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap border ${folderSelected ? 'bg-green-50 hover:bg-green-100 border-green-300 text-green-700' : 'bg-gray-100 hover:bg-gray-200 border-gray-300'
                                        }`}
                                    onClick={async () => {
                                        if (!('showDirectoryPicker' in window)) {
                                            alert('Your browser blocked folder selection! The File System Access API requires a Secure Context (HTTPS or localhost). If you are accessing this on AWS via HTTP, the browser will permanently disable the Browse button. You MUST use HTTPS or localhost to use this feature.');
                                            return;
                                        }
                                        try {
                                            const handle = await (window as any).showDirectoryPicker({ mode: 'readwrite' });
                                            const key = data.id || data.nodeId || 'file_output';
                                            
                                            // Save to in-memory cache to preserve the active 'granted' permission state
                                            (window as any)._activeHandles = (window as any)._activeHandles || {};
                                            (window as any)._activeHandles[key] = handle;
                                            
                                            try {
                                                const openReq = indexedDB.open('aap_file_output', 1);
                                                openReq.onupgradeneeded = () => openReq.result.createObjectStore('fileOutputHandles');
                                                openReq.onsuccess = () => {
                                                    const db = openReq.result;
                                                    const tx = db.transaction('fileOutputHandles', 'readwrite');
                                                    tx.objectStore('fileOutputHandles').put(handle, key);
                                                };
                                            } catch (e) { console.warn('IDB save failed:', e); }
                                            onChange('_handleKey', key);
                                            setFolderSelected(true);
                                            if (!localDownloadPath) {
                                                setLocalDownloadPath(handle.name);
                                                onChange('downloadPath', handle.name);
                                            }
                                        } catch (e: any) {
                                            if (e.name !== 'AbortError') alert('Could not open folder picker: ' + e.message);
                                        }
                                    }}
                                >
                                    {folderSelected ? '✓ Folder Selected' : 'Browse'}
                                </button>
                                {(localDownloadPath || folderSelected) && (
                                    <button
                                        type="button"
                                        className="px-2 py-1.5 text-xs text-red-500 hover:text-red-700 border border-red-200 rounded-md transition-colors"
                                        onClick={() => {
                                            setLocalDownloadPath('');
                                            setFolderSelected(false);
                                            onChange('downloadPath', '');
                                            onChange('_handleKey', '');
                                        }}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                                Type the full folder path for your reference, then click <strong>Browse</strong> to authorize the browser to save files there.
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        <Label>
                            {storageType === 's3' && 'S3 Bucket Path'}
                            {storageType === 'azure' && 'Azure Container Path'}
                            {storageType === 'sharepoint' && 'SharePoint Folder Path'}
                            {storageType === 'gdrive' && 'Google Drive Folder ID'}
                            {storageType === 'onedrive' && 'OneDrive Path'}
                        </Label>
                        <Input
                            value={data.destinationPath || ''}
                            onChange={(e) => onChange('destinationPath', e.target.value)}
                            placeholder={
                                storageType === 's3' ? 's3://bucket-name/prefix' :
                                    storageType === 'azure' ? 'container-name/path' :
                                        storageType === 'sharepoint' ? '/Shared Documents/Generated' :
                                            storageType === 'gdrive' ? 'Folder ID from Google Drive' :
                                                storageType === 'onedrive' ? '/Documents/Output' : 'Enter path'
                            }
                        />
                    </>
                )}
            </div>

            {/* File Name */}
            <div>
                <Label>File Name</Label>
                <Input
                    value={data.fileName || 'export'}
                    onChange={(e) => onChange('fileName', e.target.value)}
                    placeholder="export_report"
                />
                <p className="text-xs text-gray-500 mt-1">File name (extension added automatically based on format)</p>
            </div>

            {/* 4. Source Field (Optional) */}
            <div>
                <Label>Source Field <span className="text-gray-400 font-normal">(Optional)</span></Label>
                <Input
                    value={data.sourceField || ''}
                    onChange={(e) => onChange('sourceField', e.target.value)}
                    placeholder="output_file_path or rendered_output"
                />
                <p className="text-xs text-gray-500 mt-1">
                    Field from the previous node containing file paths or content. Leave blank to export the full table.
                </p>
            </div>

            {/* SharePoint Credentials */}
            {storageType === 'sharepoint' && (
                <div className="space-y-4">
                    <ServiceConnectionSelect
                        service="sharepoint"
                        value={data.connectionId}
                        onChange={(v) => onChange('connectionId', v)}
                        label="SharePoint Credentials"
                    />

                    {(!data.connectionId || data.connectionId === 'new') && (
                        <>
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                                <p className="text-xs text-purple-900 font-medium mb-1">Microsoft Graph API Connection</p>
                                <p className="text-xs text-purple-800">
                                    Uses Azure App Registration with Microsoft Graph API permissions.
                                </p>
                            </div>
                            <div>
                                <Label>Tenant ID<span className="text-gray-400 font-normal ml-1">(Optional)</span></Label>
                                <Input
                                    value={data.tenantId || ''}
                                    onChange={(e) => onChange('tenantId', e.target.value)}
                                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                />
                            </div>
                            <div>
                                <Label>App Client ID<span className="text-gray-400 font-normal ml-1">(Optional)</span></Label>
                                <Input
                                    value={data.clientId || ''}
                                    onChange={(e) => onChange('clientId', e.target.value)}
                                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                />
                            </div>
                            <div>
                                <Label>App Client Secret<span className="text-gray-400 font-normal ml-1">(Optional)</span></Label>
                                <Input
                                    type="password"
                                    value={data.clientSecret || ''}
                                    onChange={(e) => onChange('clientSecret', e.target.value)}
                                    placeholder="Client secret value"
                                />
                            </div>
                        </>
                    )}

                    <div>
                        <Label>SharePoint Domain<span className="text-red-500">*</span></Label>
                        <div className="flex items-center gap-1">
                            <Input
                                value={data.sharepointDomain || ''}
                                onChange={(e) => onChange('sharepointDomain', e.target.value)}
                                placeholder="contoso"
                                className="flex-1"
                            />
                            <span className="text-sm text-gray-500">.sharepoint.com</span>
                        </div>
                    </div>
                    <div>
                        <Label>Site Name<span className="text-red-500">*</span></Label>
                        <Input
                            value={data.siteName || ''}
                            onChange={(e) => onChange('siteName', e.target.value)}
                            placeholder="TeamSite"
                        />
                    </div>
                    <div>
                        <Label>Document Library</Label>
                        <Input
                            value={data.libraryName || 'Documents'}
                            onChange={(e) => onChange('libraryName', e.target.value)}
                            placeholder="Documents"
                        />
                    </div>
                </div>
            )}

            {storageType === 's3' && (
                <div className="space-y-4">
                    <ServiceConnectionSelect
                        service="aws_s3"
                        value={data.connectionId}
                        onChange={(v) => onChange('connectionId', v)}
                        label="AWS S3 Credentials"
                    />

                    {(!data.connectionId || data.connectionId === 'new') && (
                        <>
                            <div>
                                <Label>AWS Access Key ID</Label>
                                <Input
                                    type="password"
                                    value={data.awsAccessKey || ''}
                                    onChange={(e) => onChange('awsAccessKey', e.target.value)}
                                    placeholder="Or use AWS_ACCESS_KEY_ID env"
                                />
                            </div>
                            <div>
                                <Label>AWS Secret Access Key</Label>
                                <Input
                                    type="password"
                                    value={data.awsSecretKey || ''}
                                    onChange={(e) => onChange('awsSecretKey', e.target.value)}
                                    placeholder="Or use AWS_SECRET_ACCESS_KEY env"
                                />
                            </div>
                            <div>
                                <Label>AWS Region</Label>
                                <Input
                                    value={data.awsRegion || 'us-east-1'}
                                    onChange={(e) => onChange('awsRegion', e.target.value)}
                                    placeholder="us-east-1"
                                />
                            </div>
                        </>
                    )}

                    <div>
                        <Label>Bucket Name</Label>
                        <Input
                            value={data.bucketName || ''}
                            onChange={(e) => onChange('bucketName', e.target.value)}
                            placeholder="my-bucket"
                        />
                    </div>
                </div>
            )}

            {storageType === 'azure' && (
                <div className="space-y-4">
                    <ServiceConnectionSelect
                        service="azure_blob"
                        value={data.connectionId}
                        onChange={(v) => onChange('connectionId', v)}
                        label="Azure Blob Credentials"
                    />

                    {(!data.connectionId || data.connectionId === 'new') && (
                        <div>
                            <Label>Azure Storage Connection String</Label>
                            <Input
                                type="password"
                                value={data.azureConnectionString || ''}
                                onChange={(e) => onChange('azureConnectionString', e.target.value)}
                                placeholder="DefaultEndpointsProtocol=https;AccountName=..."
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Get from Azure Portal -&gt; Storage Account -&gt; Access Keys
                            </p>
                        </div>
                    )}
                </div>
            )}

            {storageType === 'gdrive' && (
                <>
                    <div>
                        <Label>Folder ID<span className="text-red-500">*</span></Label>
                        <Input
                            value={data.gdriveFolderId || ''}
                            onChange={(e) => onChange('gdriveFolderId', e.target.value)}
                            placeholder="Folder ID from drive.google.com/drive/folders/XXXXX"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Right-click folder -&gt; Share -&gt; Copy link. ID is the string after /folders/
                        </p>
                    </div>
                    <div>
                        <Label>Service Account JSON<span className="text-red-500">*</span></Label>
                        <Textarea
                            value={data.gdriveServiceAccountKey || ''}
                            onChange={(e) => onChange('gdriveServiceAccountKey', e.target.value)}
                            placeholder='Paste full JSON from Google Cloud Console'
                            rows={4}
                            className="font-mono text-xs"
                        />
                    </div>
                </>
            )}

            {storageType === 'onedrive' && (
                <div>
                    <Label>OneDrive Access Token</Label>
                    <Input
                        type="password"
                        value={data.onedriveAccessToken || ''}
                        onChange={(e) => onChange('onedriveAccessToken', e.target.value)}
                        placeholder="Paste your OneDrive/Microsoft Graph access token"
                    />
                </div>
            )}



            <div className="flex items-center space-x-2 mt-4">
                <input
                    type="checkbox"
                    id="preserveStructure"
                    checked={data.preserveStructure || false}
                    onChange={(e) => onChange('preserveStructure', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="preserveStructure" className="text-sm">Preserve directory structure</Label>
            </div>

            {/* Output Field Information */}
            <div className="bg-gray-50 p-3 rounded-md border mt-4">
                <p className="text-sm font-medium text-gray-700">Output Fields</p>
                <ul className="text-xs text-gray-600 mt-1 space-y-1">
                    <li>-<code>upload_status</code>or<code>export_status</code>- success or error message</li>
                    <li>-<code>destination_url</code>or<code>export_path</code>- URL/path of saved file</li>
                    <li>-<code>uploaded_at</code>or<code>exported_at</code>- Timestamp</li>
                    <li>-<code>rows_exported</code>- Number of rows (for table exports)</li>
                </ul>
            </div>

            {/* Multi-User Support Notice */}
            {(storageType !== 'local') && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-3">
                    <p className="text-xs text-green-900 font-medium mb-1">Multi-User Support</p>
                    <p className="text-xs text-green-800">
                        Each workflow can have its own credentials. Your credentials are stored securely with this workflow.
                    </p>
                </div>
            )}
        </div>
    );
};


// ==========================================
// SharePoint Nodes Configuration
// ==========================================

export const SharePointAccessConfig = ({ data, onChange }: BaseConfigProps) => {
    return (
        <div className="space-y-4">
            <div className="space-y-2">
                <Label className="text-sm font-medium">Global Connection ID</Label>
                <Input
                    value={data.connectionId || ''}
                    onChange={(e) => onChange('connectionId', e.target.value)}
                    placeholder="e.g. gc_sharepoint_123"
                    className="h-8 text-sm"
                />
                <p className="text-[10px] text-gray-500">Need a valid SharePoint connection setup in integrations</p>
            </div>

            <div className="space-y-2 mt-4">
                <Label className="text-sm font-medium">Operation</Label>
                <Select
                    value={data.operation || 'list'}
                    onValueChange={(val) => onChange('operation', val)}
                >
                    <SelectTrigger className="h-8">
                        <SelectValue placeholder="Select operation" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="list">List Folder Contents</SelectItem>
                        <SelectItem value="get">Get Item Metadata</SelectItem>
                        <SelectItem value="download">Download File</SelectItem>
                        <SelectItem value="upload">Upload File (requires local path)</SelectItem>
                        <SelectItem value="create_folder">Create Folder</SelectItem>
                        <SelectItem value="delete">Delete Item</SelectItem>
                        <SelectItem value="move">Move Item</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="space-y-2 mt-4">
                <Label className="text-sm font-medium">Item Path (Templated)</Label>
                <Input
                    value={data.path || ''}
                    onChange={(e) => onChange('path', e.target.value)}
                    placeholder="e.g. /Cases/{{ticket_number}}"
                    className="h-8 text-sm"
                />
                <p className="text-[10px] text-gray-500">Use {'{{column_name}}'} to insert values from upstream</p>
            </div>

            {data.operation === 'move' && (
                <div className="space-y-2 mt-4">
                    <Label className="text-sm font-medium">Destination Parent Path</Label>
                    <Input
                        value={data.destinationPath || ''}
                        onChange={(e) => onChange('destinationPath', e.target.value)}
                        placeholder="e.g. /Archive"
                        className="h-8 text-sm"
                    />
                </div>
            )}

            <div className="space-y-2 mt-4">
                <Label className="text-sm font-medium">Output Field Name</Label>
                <Input
                    value={data.outputField || 'sharepoint_result'}
                    onChange={(e) => onChange('outputField', e.target.value)}
                    placeholder="sharepoint_result"
                    className="h-8 text-sm"
                />
            </div>

            <div className="bg-blue-50 p-3 rounded-md border border-blue-200 mt-4">
                <p className="text-xs text-blue-800">
                    <strong>Note:</strong> Upstream data rows will be passed through with
                    new columns: <code>{data.outputField || 'sharepoint_result'}</code>,<code>sharepoint_status</code>,
                    and <code>sharepoint_error</code>.
                </p>
            </div>
        </div>
    );
};

export const SharePointSearchConfig = ({ data, onChange }: BaseConfigProps) => {
    return (
        <div className="space-y-4">
            <div className="space-y-2">
                <Label className="text-sm font-medium">Global Connection ID</Label>
                <Input
                    value={data.connectionId || ''}
                    onChange={(e) => onChange('connectionId', e.target.value)}
                    placeholder="e.g. gc_sharepoint_123"
                    className="h-8 text-sm"
                />
            </div>

            <div className="space-y-2 mt-4">
                <Label className="text-sm font-medium">Search Mode</Label>
                <Select
                    value={data.searchMode || 'filename'}
                    onValueChange={(val) => onChange('searchMode', val)}
                >
                    <SelectTrigger className="h-8">
                        <SelectValue placeholder="Select search mode" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="filename">Filename Search (Drive API)</SelectItem>
                        <SelectItem value="kql">KQL Full-text Search (Graph Search API)</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="space-y-2 mt-4">
                <Label className="text-sm font-medium">Search Query (Templated)</Label>
                <Textarea
                    value={data.query || ''}
                    onChange={(e) => onChange('query', e.target.value)}
                    placeholder={data.searchMode === 'kql' ? 'e.g. "ticket_id:{{ticketId}}"' : 'e.g. TKT-{{ticket_number}}'}
                    className="min-h-[80px] text-sm resize-y"
                />
                <p className="text-[10px] text-gray-500">Supports {'{{column_name}}'} dynamic replacement</p>
            </div>

            <div className="space-y-2 mt-4">
                <Label className="text-sm font-medium">Output Field Name</Label>
                <Input
                    value={data.outputField || 'search_results'}
                    onChange={(e) => onChange('outputField', e.target.value)}
                    placeholder="search_results"
                    className="h-8 text-sm"
                />
            </div>

            <div className="bg-gray-50 p-3 rounded-md border mt-4">
                <p className="text-sm font-medium text-gray-700">Output Columns</p>
                <ul className="text-xs text-gray-600 mt-1 space-y-1">
                    <li>-<code>{data.outputField || 'search_results'}</code>- JSON array of matched items</li>
                    <li>-<code>first_match_id</code>- ID of the first result</li>
                    <li>-<code>first_match_path</code>- Web URL of the first result</li>
                    <li>-<code>search_status</code>/<code>search_error</code></li>
                </ul>
            </div>
        </div>
    );
};
